import React, { useState, useEffect } from 'react';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useNavigate } from 'react-router-dom';
import Cookies from 'js-cookie';
import SignatureModal from '../CHECK-IN/Reservation/Booking-Options/SignatureModal';
import SignaturePrompt from '../CHECK-IN/Reservation/Booking-Options/SignaturePrompt';

const BQ_BASE_URL = process.env.REACT_APP_BQ_BASE_URL;

const REACT_APP_RAZORPAY_KEY = process.env.REACT_APP_RAZORPAY_KEY;

const CheckOutPayment = () => {
  const [billingSummary, setBillingSummary] = useState([]);
  const [checkoutAmount, setCheckoutAmount] = useState(0);
  const [isProcessing, setIsProcessing] = useState(false);
  const [razorpayLoaded, setRazorpayLoaded] = useState(false);
  const [bookingDetails, setBookingDetails] = useState({
    checkInDate: '',
    checkOutDate: '',
    roomType: '',
    roomNumber: '',
  });
  const [guestDetails, setGuestDetails] = useState({
    guestName: '',
    guestPhone: '',
    guestEmail: '',
    guestClientType: '',
    companyName: '',
    companyId: '',
  });
  const [hotelDetails, setHotelDetails] = useState({
    hotelName: '',
    address: '',
    phone: '',
    email: '',
  });
  const [billingTotals, setBillingTotals] = useState({
    total_final_amount: 0,
    total_base_amount: 0,
    total_tax_amount: 0,
    total_paid_amount: 0,
    pending_amount: 0,
    total_discount: 0,
    gross_billed: 0,
    total_credits: 0,
    net_billed: 0,
    total_refunded_amount: 0,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expandedRows, setExpandedRows] = useState({});
  const [serviceDetails, setServiceDetails] = useState({
    amenities: [],
    room_services: [],
    food_orders: [],
  });
  const [showPaymentOptions, setShowPaymentOptions] = useState(false);
  const [selectedPaymentOption, setSelectedPaymentOption] = useState(null);
  const [isCorporateGuest, setIsCorporateGuest] = useState(false);
  const [apiData, setApiData] = useState(null);
  const [showErrorPopup, setShowErrorPopup] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  // Signature states
  const [showSignaturePrompt, setShowSignaturePrompt] = useState(false);
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  const [signatureData, setSignatureData] = useState(null);
  const [pendingAction, setPendingAction] = useState(null);
  const [hasExistingSignature, setHasExistingSignature] = useState(false);
  const [isCheckingSignature, setIsCheckingSignature] = useState(false);

  const navigate = useNavigate();

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  const getGuestIdFromToken = (token) => {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.sub;
    } catch (e) {
      console.error('Error parsing token:', e);
      return null;
    }
  };

  const toggleRowExpand = (index) => {
    setExpandedRows((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })
      .format(amount)
      .replace('₹', '₹');
  };

  // Check if signature already exists for this order
  const checkExistingSignature = async (orderId) => {
    if (!orderId) return false;

    setIsCheckingSignature(true);
    try {
      const accessToken = Cookies.get('access_token');
      const response = await fetch(`${BQ_BASE_URL}/bq/api/get-signature/${orderId}?format=base64`, {
        method: 'GET',
        headers: {
          Accept: 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (response.status === 200) {
        const data = await response.json();
        if (data.success && data.signature) {
          setHasExistingSignature(true);
          setSignatureData(data.signature);
          toast.info('Signature already exists for this booking', {
            position: 'top-center',
            autoClose: 3000,
          });
          return true;
        }
      } else if (response.status === 404) {
        // No signature found - this is expected
        setHasExistingSignature(false);
        return false;
      }
    } catch (error) {
      console.error('Error checking signature:', error);
      // Don't block the flow if check fails
    } finally {
      setIsCheckingSignature(false);
    }
    return false;
  };

  // Save signature to backend
  const saveSignatureToBackend = async (signatureDataURL, orderId, guestName) => {
    try {
      // Convert data URL to blob
      const response = await fetch(signatureDataURL);
      const blob = await response.blob();

      const formData = new FormData();
      formData.append('order_id', orderId);
      formData.append('guest_name', guestName);
      formData.append('signature', blob, `signature_${Date.now()}.png`);
      formData.append('storage_type', 'cloud');

      const accessToken = Cookies.get('access_token');
      const saveResponse = await fetch(`${BQ_BASE_URL}/bq/api/save-signature`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
        body: formData,
      });

      if (saveResponse.ok) {
        const result = await saveResponse.json();
        console.log('Signature saved successfully:', result);
        toast.success('Signature saved successfully!', {
          position: 'top-center',
          autoClose: 3000,
        });
        return true;
      } else {
        const error = await saveResponse.json();
        console.error('Failed to save signature:', error);
        toast.error('Failed to save signature. Please try again.', {
          position: 'top-center',
          autoClose: 3000,
        });
        return false;
      }
    } catch (error) {
      console.error('Error saving signature:', error);
      toast.error('Failed to save signature. Please try again.', {
        position: 'top-center',
        autoClose: 3000,
      });
      return false;
    }
  };

  const extractServiceName = (description) => {
    return description
      .replace(/^(Room Service|Amenity Item|Amenity Services|Food Order)\s*-\s*/i, '')
      .split(' x ')[0];
  };

  const extractQuantity = (description) => {
    const match = description.match(/x\s*(\d+)/i);
    return match ? parseInt(match[1], 10) : 1;
  };

  const transformBillingData = (data) => {
    const billingItems = [];
    const processedItems = new Set();

    const roomServicesFromOrders = data.order_details?.['Room Services'] || [];

    if (roomServicesFromOrders.length > 0) {
      const itemsData = roomServicesFromOrders.map((order) => ({
        ...order,
        base_amount: order.amount,
        tax_amount: order.tax,
        discount: order.discount || 0,
        total_amount: order.total_amount,
        billing_type: order.order_type,
        is_paid: order.payment_status === 'Paid',
        payment_status: order.payment_status,
        name: extractServiceName(order.description),
        quantity: extractQuantity(order.description),
        created_at: order.created_at,
        description: order.description,
      }));

      const totalBase = itemsData.reduce((sum, item) => sum + item.base_amount, 0);
      const totalTax = itemsData.reduce((sum, item) => sum + item.tax_amount, 0);
      const totalDiscount = itemsData.reduce((sum, item) => sum + (item.discount || 0), 0);
      const totalAmount = itemsData.reduce((sum, item) => sum + item.total_amount, 0);
      const isPaid = itemsData.every((item) => item.is_paid);

      billingItems.push({
        billing_id: 'room-services-group',
        billing_type: 'Room Services',
        base_amount: totalBase,
        tax_amount: totalTax,
        discount_amount: totalDiscount,
        total_amount: totalAmount,
        is_paid: isPaid,
        payment_status: isPaid ? 'Paid' : 'Pending',
        isGroup: true,
        groupItems: itemsData,
        sortOrder: 2,
      });
    }

    const amenityServicesFromOrders = data.order_details?.['Amenity Services'] || [];

    if (amenityServicesFromOrders.length > 0) {
      const itemsData = amenityServicesFromOrders.map((order) => ({
        ...order,
        base_amount: order.amount,
        tax_amount: order.tax,
        discount: order.discount || 0,
        total_amount: order.total_amount,
        billing_type: order.order_type,
        is_paid: order.payment_status === 'Paid',
        payment_status: order.payment_status,
        name: extractServiceName(order.description),
        quantity: extractQuantity(order.description),
        created_at: order.created_at,
        description: order.description,
      }));

      const totalBase = itemsData.reduce((sum, item) => sum + item.base_amount, 0);
      const totalTax = itemsData.reduce((sum, item) => sum + item.tax_amount, 0);
      const totalDiscount = itemsData.reduce((sum, item) => sum + (item.discount || 0), 0);
      const totalAmount = itemsData.reduce((sum, item) => sum + item.total_amount, 0);
      const isPaid = itemsData.every((item) => item.is_paid);

      billingItems.push({
        billing_id: 'amenity-services-group',
        billing_type: 'Amenity Service',
        base_amount: totalBase,
        tax_amount: totalTax,
        discount_amount: totalDiscount,
        total_amount: totalAmount,
        is_paid: isPaid,
        payment_status: isPaid ? 'Paid' : 'Pending',
        isGroup: true,
        groupItems: itemsData,
        sortOrder: 3,
      });
    }

    const foodOrdersFromOrders = data.order_details?.['Food'] || [];

    if (foodOrdersFromOrders.length > 0) {
      const itemsData = foodOrdersFromOrders.map((order) => ({
        ...order,
        base_amount: order.amount,
        tax_amount: order.tax,
        discount: order.discount || 0,
        total_amount: order.total_amount,
        billing_type: order.order_type,
        is_paid: order.payment_status === 'Paid',
        payment_status: order.payment_status,
        name: extractServiceName(order.description),
        quantity: extractQuantity(order.description),
        created_at: order.created_at,
        description: order.description,
      }));

      const totalBase = itemsData.reduce((sum, item) => sum + item.base_amount, 0);
      const totalTax = itemsData.reduce((sum, item) => sum + item.tax_amount, 0);
      const totalDiscount = itemsData.reduce((sum, item) => sum + (item.discount || 0), 0);
      const totalAmount = itemsData.reduce((sum, item) => sum + item.total_amount, 0);
      const isPaid = itemsData.every((item) => item.is_paid);

      billingItems.push({
        billing_id: 'food-orders-group',
        billing_type: 'Food Orders',
        base_amount: totalBase,
        tax_amount: totalTax,
        discount_amount: totalDiscount,
        total_amount: totalAmount,
        is_paid: isPaid,
        payment_status: isPaid ? 'Paid' : 'Pending',
        isGroup: true,
        groupItems: itemsData,
        sortOrder: 4,
      });
    }

    const roomChargesFromOrderDetails = data.order_details?.['Room Charges'] || [];

    if (roomChargesFromOrderDetails.length > 0) {
      const totalBase = roomChargesFromOrderDetails.reduce((sum, item) => sum + item.amount, 0);
      const totalTax = roomChargesFromOrderDetails.reduce((sum, item) => sum + item.tax, 0);
      const totalServiceFee = roomChargesFromOrderDetails.reduce(
        (sum, item) => sum + (item.service_fee || 0),
        0
      );
      const totalDiscount = roomChargesFromOrderDetails.reduce(
        (sum, item) => sum + (item.discount || 0),
        0
      );
      const totalAmount = roomChargesFromOrderDetails.reduce(
        (sum, item) => sum + item.total_amount,
        0
      );
      const isPaid = roomChargesFromOrderDetails.every((item) => item.payment_status === 'Paid');

      const itemsData = roomChargesFromOrderDetails.map((order) => {
        const cleanedDescription = order.description
          ? order.description.replace(/\s*\(after\s*recalculation\)/gi, '').trim()
          : order.order_type || 'Room Rent';

        return {
          ...order,
          base_amount: order.amount,
          tax_amount: order.tax,
          service_fee: order.service_fee || 0,
          discount: order.discount || 0,
          total_amount: order.total_amount,
          billing_type: order.order_type,
          is_paid: order.payment_status === 'Paid',
          payment_status: order.payment_status,
          name: cleanedDescription,
          quantity: 1,
          created_at: order.created_at,
          description: cleanedDescription,
        };
      });

      billingItems.push({
        billing_id: 'room-charges-group-from-order-details',
        billing_type: 'Room Charges',
        base_amount: totalBase,
        tax_amount: totalTax,
        service_fee: totalServiceFee,
        discount_amount: totalDiscount,
        total_amount: totalAmount,
        is_paid: isPaid,
        payment_status: isPaid ? 'Paid' : 'Pending',
        isGroup: true,
        groupItems: itemsData,
        sortOrder: 0,
      });
    }

    const securityDepositFromOrders = data.order_details?.['Security Deposit'] || [];

    if (securityDepositFromOrders.length > 0) {
      const itemsData = securityDepositFromOrders.map((order) => ({
        ...order,
        base_amount: order.amount,
        tax_amount: order.tax,
        discount: order.discount || 0,
        total_amount: order.total_amount,
        billing_type: order.order_type,
        is_paid: order.payment_status === 'Paid',
        payment_status: order.payment_status,
        name: 'Security Deposit',
        quantity: 1,
        created_at: order.created_at,
      }));

      const totalBase = itemsData.reduce((sum, item) => sum + item.base_amount, 0);
      const totalTax = itemsData.reduce((sum, item) => sum + item.tax_amount, 0);
      const totalDiscount = itemsData.reduce((sum, item) => sum + (item.discount || 0), 0);
      const totalAmount = itemsData.reduce((sum, item) => sum + item.total_amount, 0);
      const isPaid = itemsData.every((item) => item.is_paid);

      billingItems.push({
        billing_id: 'security-deposit-group',
        billing_type: 'Security Deposit',
        base_amount: totalBase,
        tax_amount: totalTax,
        discount_amount: totalDiscount,
        total_amount: totalAmount,
        is_paid: isPaid,
        payment_status: isPaid ? 'Paid' : 'Pending',
        isGroup: true,
        groupItems: itemsData,
        sortOrder: 1,
      });
    }

    const otherCharges = data.billing_details.filter((item) =>
      ['Early Check-in', 'Late Check-out', 'Other', 'Enhance stay reservation'].includes(
        item.billing_type
      )
    );

    if (otherCharges.length > 0) {
      const itemsData = otherCharges.map((item) => ({
        ...item,
        name: item.billing_type,
        quantity: 1,
        payment_status: item.is_paid ? 'Paid' : 'Pending',
        created_at: item.payment_details[0]?.date || new Date().toISOString(),
      }));

      const totalBase = itemsData.reduce((sum, item) => sum + item.base_amount, 0);
      const totalTax = itemsData.reduce((sum, item) => sum + item.tax_amount, 0);
      const totalDiscount = itemsData.reduce((sum, item) => sum + (item.discount || 0), 0);
      const totalAmount = itemsData.reduce((sum, item) => sum + item.total_amount, 0);
      const isPaid = itemsData.every((item) => item.is_paid);

      billingItems.push({
        billing_id: 'other-charges-group',
        billing_type: 'Other Charges',
        base_amount: totalBase,
        tax_amount: totalTax,
        discount_amount: totalDiscount,
        total_amount: totalAmount,
        is_paid: isPaid,
        payment_status: isPaid ? 'Paid' : 'Pending',
        isGroup: true,
        groupItems: itemsData,
        sortOrder: 5,
      });
    }

    return billingItems.sort((a, b) => {
      if (a.sortOrder !== b.sortOrder) {
        return a.sortOrder - b.sortOrder;
      }
      return a.billing_type.localeCompare(b.billing_type);
    });
  };

  const getSortOrder = (billingType) => {
    const sortOrderMap = {
      'Room Charges': 0,
      'Security Deposit': 1,
      'Room Services': 2,
      'Amenity Service': 3,
      'Food Orders': 4,
      'Other Charges': 5,
      'Checkout Adjustments': 6,
    };
    return sortOrderMap[billingType] || 99;
  };

  const getItemName = (item, orderDetails) => {
    for (const [category, items] of Object.entries(orderDetails)) {
      const matchingOrder = items.find(
        (order) =>
          Math.abs(order.amount - item.base_amount) < 0.01 &&
          Math.abs(order.tax - item.tax_amount) < 0.01
      );
      if (matchingOrder) {
        return extractServiceName(matchingOrder.description);
      }
    }
    return item.billing_type;
  };

  const getItemQuantity = (item, orderDetails) => {
    for (const [category, items] of Object.entries(orderDetails)) {
      const matchingOrder = items.find(
        (order) =>
          Math.abs(order.amount - item.base_amount) < 0.01 &&
          Math.abs(order.tax - item.tax_amount) < 0.01
      );
      if (matchingOrder) {
        return extractQuantity(matchingOrder.description);
      }
    }
    return 1;
  };

  const sendInvoiceEmail = async () => {
    try {
      const orderDetails = JSON.parse(localStorage.getItem('orderDetails'));
      const primaryGuest = JSON.parse(localStorage.getItem('primaryGuest'));

      if (!orderDetails || !primaryGuest) {
        console.warn('Order details or primary guest not found in localStorage');
        return;
      }

      const orderId = orderDetails.orderid;
      const recipientEmail = primaryGuest.emailid;

      if (!orderId || !recipientEmail) {
        console.warn('Order ID or recipient email not available');
        return;
      }

      const accessToken = Cookies.get('access_token');
      const response = await fetch(
        `${BQ_BASE_URL}/bq/api/invoice/email/${orderId}?recipient_email=${encodeURIComponent(
          recipientEmail
        )}`,
        {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to send invoice email');
      }

      const result = await response.json();
      console.log('Invoice email sent successfully:', result);
      return true;
    } catch (error) {
      console.error('Error sending invoice email:', error);
      return false;
    }
  };

  const fetchInvoiceData = async () => {
    try {
      const orderId = localStorage.getItem('orderId');
      const accessToken = Cookies.get('access_token');

      if (!orderId) {
        throw new Error('Order ID not found in localStorage');
      }

      const response = await fetch(
        `${BQ_BASE_URL}/bq/api/guest/billing-summary/?order_id=${orderId}`,
        {
          method: 'GET',
          headers: {
            Accept: 'application/json',
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch invoice data: ${response.statusText}`);
      }

      const data = await response.json();
      setApiData(data);

      const hotelCache = localStorage.getItem('hotelPropertyCache');
      if (hotelCache) {
        const hotelData = JSON.parse(hotelCache).data;
        setHotelDetails({
          hotelName: hotelData.propertyname,
          address: hotelData.address,
          phone: hotelData.phone,
          email: hotelData.email,
        });
      }

      if (data.guest_details) {
        const guestData = data.guest_details;
        setGuestDetails({
          guestName: guestData.first_name + ' ' + guestData.last_name,
          guestPhone: guestData.phone || '',
          guestEmail: guestData.email || '',
          guestClientType: guestData.client_type || 'Leisure',
          companyName: guestData.company_name || '',
          companyId: guestData.company_id || '',
        });

        setIsCorporateGuest(guestData.client_type === 'Corporate');
      } else if (data.booking) {
        setGuestDetails({
          guestName: data.booking.guest_name || '',
          guestPhone: '',
          guestEmail: '',
          guestClientType: 'Leisure',
          companyName: '',
          companyId: '',
        });
        setIsCorporateGuest(false);
      }

      if (data.booking) {
        setBookingDetails({
          checkInDate: formatDate(data.booking.checkin_date),
          checkOutDate: formatDate(data.booking.checkout_date),
          roomType: data.booking.room_type || '',
          roomNumber: data.booking.room_assigned?.room_number || 'N/A',
        });
      }

      const transformedBilling = transformBillingData(data);
      setBillingSummary(transformedBilling);

      let totalDiscount = 0;
      if (data.billing_details && Array.isArray(data.billing_details)) {
        totalDiscount = data.billing_details.reduce((sum, item) => {
          return sum + (item.discount || 0);
        }, 0);
      }

      if (data.payment_summary) {
        const netBilled = data.payment_summary.net_billed || data.payment_summary.total_amount;
        const grossBilled = data.payment_summary.gross_billed || data.payment_summary.total_amount;
        const totalCredits = data.payment_summary.total_credits || 0;
        const totalRefunded = data.payment_summary.total_refunded || 0;

        setBillingTotals({
          total_final_amount: netBilled,
          total_base_amount: netBilled,
          total_tax_amount: data.payment_summary.total_tax,
          total_paid_amount: data.payment_summary.total_paid,
          pending_amount: data.payment_summary.total_due,
          total_discount: totalDiscount,
          gross_billed: grossBilled,
          total_credits: totalCredits,
          net_billed: netBilled,
          total_refunded_amount: totalRefunded,
        });
      } else if (data.total_amounts) {
        const netBilled = data.total_amounts.net || data.total_amounts.final;
        const grossBilled = data.total_amounts.gross || data.total_amounts.final;
        const totalCredits = data.total_amounts.credits || 0;
        const totalRefunded = data.total_amounts.refunded || 0;

        setBillingTotals({
          total_final_amount: netBilled,
          total_base_amount: data.total_amounts.base,
          total_tax_amount: data.total_amounts.tax,
          total_paid_amount: data.total_amounts.paid,
          pending_amount: data.total_amounts.pending,
          total_discount: totalDiscount,
          gross_billed: grossBilled,
          total_credits: totalCredits,
          net_billed: netBilled,
          total_refunded_amount: totalRefunded,
        });
      }

      setServiceDetails({
        amenities: data.order_details?.['Amenity Services'] || [],
        room_services: data.order_details?.['Room Services'] || [],
        food_orders: data.order_details?.['Food'] || [],
      });

      if (data.payment_summary?.total_due) {
        setCheckoutAmount(data.payment_summary.total_due);
      } else if (data.total_amounts?.pending) {
        setCheckoutAmount(data.total_amounts.pending);
      }

      // After loading invoice data, check if signature exists
      await checkExistingSignature(orderId);
    } catch (error) {
      console.error('Error fetching invoice data:', error);
      toast.error('Failed to load invoice data. Please try again.');
    }
  };

  // MODIFIED: Add signature prompt for zero amount
  const handleProceedPayment = async () => {
    // If signature already exists, skip the prompt
    if (hasExistingSignature) {
      if (billingTotals.pending_amount === 0) {
        setPendingAction('checkout');
        await handleZeroAmountCheckout();
      } else {
        setShowPaymentOptions(true);
      }
      return;
    }

    if (billingTotals.pending_amount === 0) {
      // Fully paid - ask for signature before checkout
      setPendingAction('checkout');
      setShowSignaturePrompt(true);
    } else {
      // Not fully paid - show payment options
      setShowPaymentOptions(true);
    }
  };

  // ORIGINAL handleZeroAmountCheckout - KEPT EXACTLY THE SAME
  const handleZeroAmountCheckout = async () => {
    const orderId = localStorage.getItem('orderId');
    const accessToken = Cookies.get('access_token');

    if (!orderId) {
      toast.error('Order ID not found. Please try again.');
      return;
    }

    setLoading(true);

    try {
      const checkoutResponse = await fetch(`${BQ_BASE_URL}/bq/api/check-out?orderid=${orderId}`, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        credentials: 'include',
      });

      if (!checkoutResponse.ok) {
        toast.error('Checkout failed. Please try again.');
        setLoading(false);
        return;
      }

      const checkoutData = await checkoutResponse.json();

      localStorage.setItem('baseAmount', checkoutData.baseamount);
      localStorage.setItem('finalAmount', checkoutData.finalamount);
      localStorage.setItem('taxAmount', checkoutData.taxamount);

      const orderDetails = {
        orderid: orderId,
        guest: {
          guestid: getGuestIdFromToken(accessToken),
          firstname: guestDetails.guestName.split(' ')[0],
          lastname: guestDetails.guestName.split(' ')[1] || '',
          emailid: guestDetails.guestEmail,
          phonenumber: guestDetails.guestPhone,
        },
      };

      localStorage.setItem('orderDetails', JSON.stringify(orderDetails));

      await sendInvoiceEmail();

      toast.success(
        <div>
          <strong>Thank you for staying with us!</strong>
          <br />
          {signatureData ? 'Your signed invoice has been recorded. ' : ''}
          Please visit the front desk to return your keys.
          <br />
          You will receive your invoice via email shortly.
        </div>,
        {
          position: 'top-center',
          autoClose: 7000,
        }
      );

      // Clear signature states
      setSignatureData(null);
      setShowSignaturePrompt(false);
      setShowSignatureModal(false);

      navigate('/checkout-payment-success');
    } catch (error) {
      console.error('Error:', error);
      toast.error('Checkout failed. Please try again.');
      setLoading(false);
    }
  };

  // MODIFIED: Make all options show SignaturePrompt first
  const handlePaymentOptionSelect = async (option) => {
    setSelectedPaymentOption(option);

    // If signature already exists, proceed directly
    if (hasExistingSignature) {
      if (option === 'payNow') {
        await handleRazorpayPayment();
      } else if (option === 'billToCompany') {
        await handlePayLater('billToCompany');
      } else if (option === 'payAtFrontDesk') {
        await handlePayLater('payAtFrontDesk');
      }
      setShowPaymentOptions(false);
      return;
    }

    if (option === 'payNow') {
      setPendingAction('payNow');
      setShowSignaturePrompt(true);
      setShowPaymentOptions(false);
    } else if (option === 'billToCompany') {
      setPendingAction('billToCompany');
      setShowSignaturePrompt(true);
      setShowPaymentOptions(false);
    } else if (option === 'payAtFrontDesk') {
      setPendingAction('payAtFrontDesk');
      setShowSignaturePrompt(true);
      setShowPaymentOptions(false);
    }
  };

  // ORIGINAL handlePayLater - KEPT EXACTLY THE SAME
  const handlePayLater = async (option) => {
    if (isProcessing) return;

    setIsProcessing(true);
    setLoading(true);

    try {
      const orderId = localStorage.getItem('orderId');
      const accessToken = Cookies.get('access_token');

      if (!orderId) {
        throw new Error('Order ID not found');
      }

      const checkoutResponse = await fetch(`${BQ_BASE_URL}/bq/api/check-out?orderid=${orderId}`, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        credentials: 'include',
      });

      if (!checkoutResponse.ok) {
        toast.error('Checkout failed. Please try again.');
        setIsProcessing(false);
        setLoading(false);
        return;
      }

      const checkoutData = await checkoutResponse.json();

      localStorage.setItem('baseAmount', checkoutData.baseamount);
      localStorage.setItem('finalAmount', checkoutData.finalamount);
      localStorage.setItem('taxAmount', checkoutData.taxamount);

      const orderDetails = {
        orderid: orderId,
        guest: {
          guestid: getGuestIdFromToken(accessToken),
          firstname: guestDetails.guestName.split(' ')[0],
          lastname: guestDetails.guestName.split(' ')[1] || '',
          emailid: guestDetails.guestEmail,
          phonenumber: guestDetails.guestPhone,
        },
      };

      localStorage.setItem('orderDetails', JSON.stringify(orderDetails));

      setShowPaymentOptions(false);

      // Clear signature states
      setSignatureData(null);
      setShowSignaturePrompt(false);
      setShowSignatureModal(false);

      navigate('/checkout-payment-success');
    } catch (error) {
      console.error('Payment error:', error);
      toast.error('Checkout failed. Please try again.');
      setIsProcessing(false);
      setLoading(false);
    }
  };

  // ORIGINAL handleRazorpayPayment - KEPT EXACTLY THE SAME
  const handleRazorpayPayment = async () => {
    if (isProcessing) return;

    setIsProcessing(true);
    setLoading(true);

    try {
      const orderId = localStorage.getItem('orderId');
      const bookingId = localStorage.getItem('bookingId') || apiData?.booking?.booking_id;
      const accessToken = Cookies.get('access_token');

      if (!orderId || !bookingId) {
        throw new Error('Order or Booking ID not found');
      }

      const unpaidBillingIds = apiData?.payment_summary?.unpaid_billing_ids || [];

      if (unpaidBillingIds.length === 0) {
        toast.error('No unpaid billing information available. Please try again.');
        setIsProcessing(false);
        setLoading(false);
        return;
      }

      localStorage.setItem('unpaidBillingIds', JSON.stringify(unpaidBillingIds));

      if (!window.Razorpay) {
        throw new Error('Payment gateway not loaded');
      }

      const orderResponse = await fetch(
        `${BQ_BASE_URL}/bq/api/razorpay/create_payment_order_multiple?bookingid=${bookingId}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify(unpaidBillingIds),
        }
      );

      const orderData = await orderResponse.json();

      if (!orderResponse.ok || !orderData?.razorpay_order_id) {
        throw new Error('Failed to create payment order');
      }

      const razorpayAmount = orderData.amount || billingTotals.pending_amount * 100;

      const options = {
        key: REACT_APP_RAZORPAY_KEY,
        amount: razorpayAmount,
        currency: 'INR',
        name: hotelDetails.hotelName || 'Hotel Booking',
        description: 'Room Checkout Payment',
        order_id: orderData.razorpay_order_id,
        handler: async function (response) {
          const paymentDetails = {
            razorpay_order_id: response.razorpay_order_id,
            razorpay_payment_id: response.razorpay_payment_id,
            razorpay_signature: response.razorpay_signature,
            billing_ids: unpaidBillingIds,
            booking_id: bookingId,
          };

          try {
            const verificationResponse = await fetch(
              `${BQ_BASE_URL}/bq/api/razorpay/verify_payment_multiple`,
              {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  Accept: 'application/json',
                  Authorization: `Bearer ${accessToken}`,
                },
                body: JSON.stringify(paymentDetails),
              }
            );

            if (verificationResponse.ok) {
              const verificationData = await verificationResponse.json();

              if (verificationData.status === 'success') {
                const checkoutResponse = await fetch(
                  `${BQ_BASE_URL}/bq/api/check-out?orderid=${orderId}`,
                  {
                    method: 'POST',
                    headers: {
                      Accept: 'application/json',
                      'Content-Type': 'application/json',
                      Authorization: `Bearer ${accessToken}`,
                    },
                    credentials: 'include',
                  }
                );

                if (!checkoutResponse.ok) {
                  console.error('Checkout failed after successful payment');
                  toast.error('Payment succeeded but checkout failed. Please contact support.');
                  setShowPaymentOptions(false);

                  // Clear signature states
                  setSignatureData(null);
                  setShowSignaturePrompt(false);
                  setShowSignatureModal(false);

                  navigate('/checkout-payment-success');
                  return;
                }

                const checkoutData = await checkoutResponse.json();

                localStorage.setItem('baseAmount', checkoutData.baseamount);
                localStorage.setItem('finalAmount', checkoutData.finalamount);
                localStorage.setItem('taxAmount', checkoutData.taxamount);
                localStorage.setItem('bookingId', checkoutData.bookingid);
                localStorage.setItem('finalPaymentStatus', checkoutData.finalpaymentstatus);

                const orderDetails = {
                  orderid: orderId,
                  guest: {
                    guestid: getGuestIdFromToken(accessToken),
                    firstname: guestDetails.guestName.split(' ')[0],
                    lastname: guestDetails.guestName.split(' ')[1] || '',
                    emailid: guestDetails.guestEmail,
                    phonenumber: guestDetails.guestPhone,
                  },
                };

                localStorage.setItem('orderDetails', JSON.stringify(orderDetails));

                await sendInvoiceEmail();

                toast.success(
                  <div>
                    <strong>Thank you for staying with us!</strong>
                    <br />
                    {signatureData ? 'Your signed invoice has been recorded. ' : ''}
                    Please visit the front desk to return your keys.
                    <br />
                    You will receive your invoice via email shortly.
                  </div>,
                  {
                    position: 'top-center',
                    autoClose: 7000,
                  }
                );

                setShowPaymentOptions(false);

                // Clear signature states
                setSignatureData(null);
                setShowSignaturePrompt(false);
                setShowSignatureModal(false);

                navigate('/checkout-payment-success');
              } else {
                throw new Error('Payment verification failed');
              }
            } else {
              throw new Error('Payment verification failed');
            }
          } catch (error) {
            console.error('Verification error:', error);
            toast.error('Payment verification failed. Please contact support.');
            setIsProcessing(false);
            setLoading(false);
          }
        },
        modal: {
          ondismiss: function () {
            console.log('Payment modal closed by user');
            setIsProcessing(false);
            setLoading(false);
            toast.info('Payment cancelled');
          },
        },
        prefill: {
          name: guestDetails.guestName,
          email: guestDetails.guestEmail,
          contact: guestDetails.guestPhone?.toString() || '',
        },
        theme: { color: '#3399cc' },
        notes: {
          bookingId: bookingId,
          billingIds: unpaidBillingIds.join(','),
          orderId: orderId,
        },
      };

      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (error) {
      console.error('Payment error:', error);
      toast.error('Payment failed. Please try again.');
      setIsProcessing(false);
      setLoading(false);
    }
  };

  // UPDATED: Handle signature confirmation with backend save
  const handleSignatureConfirm = async (signatureDataURL) => {
    setSignatureData(signatureDataURL);
    setShowSignatureModal(false);
    setShowSignaturePrompt(false);

    // Save signature to backend
    const orderId = localStorage.getItem('orderId');
    if (orderId && signatureDataURL) {
      const saved = await saveSignatureToBackend(signatureDataURL, orderId, guestDetails.guestName);
      if (saved) {
        setHasExistingSignature(true);
      }
    }

    // Execute the pending action after signature is captured
    if (pendingAction === 'checkout') {
      handleZeroAmountCheckout();
    } else if (pendingAction === 'payNow') {
      handleRazorpayPayment();
    } else if (pendingAction === 'billToCompany') {
      handlePayLater('billToCompany');
    } else if (pendingAction === 'payAtFrontDesk') {
      handlePayLater('payAtFrontDesk');
    }
  };

  // NEW: Handle skip signature
  const handleSkipSignature = () => {
    setShowSignaturePrompt(false);

    // Execute the pending action without signature
    if (pendingAction === 'checkout') {
      handleZeroAmountCheckout();
    } else if (pendingAction === 'payNow') {
      handleRazorpayPayment();
    } else if (pendingAction === 'payAtFrontDesk') {
      handlePayLater('payAtFrontDesk');
    }
    // For billToCompany, signature is required so we don't skip
  };

  // FIX #1 & #2: Updated renderServiceDetails with discount column and proper column headers
  const renderServiceDetails = (billingType, groupItems) => {
    const getItemValues = (item) => {
      return {
        base: item.base_amount !== undefined ? item.base_amount : item.amount,
        tax: item.tax_amount !== undefined ? item.tax_amount : item.tax,
        service_fee: item.service_fee || 0,
        discount: item.discount || 0,
        total: item.total_amount,
        date: item.created_at,
      };
    };

    const renderTable = (items, columns, showQuantity = true) => {
      return (
        <div className="mt-2 ml-4 p-2 bg-gray-50 rounded">
          <h4 className="font-semibold mb-2">{billingType} Details</h4>
          <table className="w-full">
            <thead>
              <tr className="bg-gray-100">
                {columns.map((col, idx) => (
                  <th key={idx} className="text-left p-2 text-sm">
                    {col}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {items.map((item, idx) => {
                const values = getItemValues(item);

                const displayName =
                  billingType === 'Room Charges'
                    ? item.description || item.name || item.billing_type
                    : item.name || extractServiceName(item.description || item.billing_type);

                return (
                  <tr key={idx} className="border-b">
                    <td className="p-2 text-sm">{displayName}</td>
                    <td className="p-2 text-sm">{formatDate(values.date)}</td>
                    {showQuantity && <td className="p-2 text-sm">{item.quantity || 1}</td>}
                    <td className="p-2 text-sm">{formatCurrency(values.base)}</td>
                    <td className="p-2 text-sm">{formatCurrency(values.tax)}</td>
                    {/* FIX #1: Added Discount column for all expandable details */}
                    <td className="p-2 text-sm text-green-600">
                      {billingType === 'Room Charges' && values.discount !== 0
                        ? formatCurrency(values.discount)
                        : billingType !== 'Room Charges' && values.discount !== 0
                          ? formatCurrency(values.discount)
                          : formatCurrency(0)}
                    </td>
                    {columns.includes('Service Fee') && (
                      <td className="p-2 text-sm">{formatCurrency(values.service_fee)}</td>
                    )}
                    <td className="p-2 text-sm font-semibold">{formatCurrency(values.total)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      );
    };

    if (billingType === 'Room Charges') {
      // FIX #1: Added "Discount" column and removed separate Discount column from array if present
      return renderTable(
        groupItems,
        ['Description', 'Date', 'Quantity', 'Price', 'Tax', 'Discount', 'Service Fee', 'Total'],
        true
      );
    }

    // FIX #1: Added "Discount" column for all service types
    return renderTable(
      groupItems,
      ['Item', 'Date', 'Quantity', 'Price', 'Tax', 'Discount', 'Total'],
      true
    );
  };

  const buttonText =
    billingTotals.pending_amount === 0 ? 'Checkout' : loading ? 'Processing...' : 'Proceed Payment';

  useEffect(() => {
    fetchInvoiceData();

    const loadRazorpayScript = () => {
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.async = true;
      script.onload = () => setRazorpayLoaded(true);
      script.onerror = () => {
        toast.error('Error loading Razorpay script. Please try again.');
      };
      document.body.appendChild(script);
    };

    if (!window.Razorpay) {
      loadRazorpayScript();
    } else {
      setRazorpayLoaded(true);
    }
  }, []);

  return (
    <div className="flex w-full bg-gray-50 p-2 flex-col">
      <div className="mb-6">
        <h1 className="text-xl font-bold mb-1 text-gray-900">Payment</h1>
        <p className="text-gray-600 text-sm mb- mt-">Review and complete your payment</p>
      </div>

      <div className="flex w-full">
        <main className="flex-1 overflow-y-auto mr-4">
          <div
            className="bg-white p-6 rounded-lg shadow-md flex flex-col"
            style={{ minHeight: 'calc(100vh - 8rem)' }}
          >
            <div className="flex-grow">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10">
                <div className="flex flex-col gap-2">
                  <p className="text-xl font-bold text-black">Guest Details</p>
                  <div className="grid grid-cols-1 gap-y-2 text-gray-800">
                    <div>
                      <span className="font-semibold">Name:</span> {guestDetails.guestName}
                    </div>
                    {guestDetails.guestEmail && (
                      <div>
                        <span className="font-semibold">Email:</span> {guestDetails.guestEmail}
                      </div>
                    )}
                    <div>
                      <span className="font-semibold">Client Type:</span>{' '}
                      {guestDetails.guestClientType}
                    </div>
                    {isCorporateGuest && guestDetails.companyName && (
                      <div>
                        <span className="font-semibold">Company:</span> {guestDetails.companyName}
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex flex-col gap-2">
                  <p className="text-xl font-bold text-black">Reservation Details</p>
                  <div className="grid grid-cols-2 gap-x-8 gap-y-2 text-gray-800">
                    <div className="font-semibold">Room No: {bookingDetails.roomNumber}</div>
                    <div className="font-semibold">Room Type: {bookingDetails.roomType}</div>
                    <div className="font-semibold">Check-In: {bookingDetails.checkInDate}</div>
                    <div className="font-semibold">Check-Out: {bookingDetails.checkOutDate}</div>
                  </div>
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="py-3 px-4 text-left text-base font-medium text-gray-600">
                        Billing Type
                      </th>
                      <th className="py-3 px-4 text-left text-base font-medium text-gray-600">
                        Price
                      </th>
                      <th className="py-3 px-4 text-left text-base font-medium text-gray-600">
                        Tax
                      </th>
                      <th className="py-3 px-4 text-left text-base font-medium text-gray-600">
                        Discount
                      </th>
                      <th className="py-3 px-4 text-left text-base font-medium text-gray-600">
                        Service Fee
                      </th>
                      <th className="py-3 px-4 text-left text-base font-medium text-gray-600">
                        Final Price
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {billingSummary.map((item, index) => (
                      <React.Fragment key={index}>
                        <tr
                          className="border-b hover:bg-gray-50 transition-colors cursor-pointer"
                          onClick={() => toggleRowExpand(index)}
                        >
                          <td className="py-3 px-4 text-base text-gray-700">
                            {item.billing_type}
                            {item.isGroup && (
                              <span className="ml-2 text-gray-500">
                                ({expandedRows[index] ? 'Hide' : 'Show'} details)
                              </span>
                            )}
                          </td>
                          <td className="py-3 px-4 text-base text-gray-700">
                            {formatCurrency(item.base_amount)}
                          </td>
                          <td className="py-3 px-4 text-base text-gray-700">
                            {formatCurrency(item.tax_amount)}
                          </td>
                          <td className="py-3 px-4 text-base text-green-600">
                            {formatCurrency(item.discount_amount || 0)}
                          </td>
                          <td className="py-3 px-4 text-base text-gray-700">
                            {item.billing_type === 'Room Charges'
                              ? formatCurrency(item.service_fee || 0)
                              : '-'}
                          </td>
                          <td className="py-3 px-4 text-base text-gray-700">
                            {formatCurrency(item.total_amount)}
                          </td>
                        </tr>
                        {expandedRows[index] && item.isGroup && (
                          <tr>
                            <td colSpan="6" className="p-0">
                              {renderServiceDetails(item.billing_type, item.groupItems)}
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="mt-auto pt-4 border-t border-gray-200">
              <h3 className="text-base font-semibold text-gray-600">Terms & Conditions</h3>
              <p className="text-base text-gray-500">
                Payment is due within 7 days of checkout. Late payments may incur additional fees.
              </p>
            </div>
          </div>
        </main>

        {/* FIX #2 & #3: Updated Payment Summary sidebar */}
        <div className="w-1/4">
          <div className="bg-white p-6 rounded-lg shadow-md flex flex-col">
            <h2 className="text-xl font-bold mb-4">Payment Summary</h2>
            <div className="space-y-2 flex-grow">
              {/* FIX #2: Show Gross Billed instead of Total Amount */}
              <div className="flex justify-between">
                <span>Total Amount</span>
                <span>{formatCurrency(billingTotals.gross_billed)}</span>
              </div>

              {/* {billingTotals.total_discount > 0 && (
                <div className="flex justify-between text-green-600">
                  <span>Total Discount</span>
                  <span>{formatCurrency(billingTotals.total_discount)}</span>
                </div>
              )} */}

              {/* FIX #3: Added Credits/Adjustments section */}
              {billingTotals.total_credits !== 0 && billingTotals.total_credits < 0 && (
                <div className="flex justify-between text-green-600">
                  <span>Total Discount</span>
                  <span>{formatCurrency(Math.abs(billingTotals.total_credits))}</span>
                </div>
              )}

              <div className="flex justify-between">
                <span>Amount Paid</span>
                <span>{formatCurrency(billingTotals.total_paid_amount)}</span>
              </div>

              {/* FIX #3: Show Net Billed */}
              {/* <div className="flex justify-between">
                <span>Net Billed</span>
                <span>{formatCurrency(billingTotals.net_billed)}</span>
              </div> */}
            </div>

            <hr className="my-3" />

            <div className="flex justify-between">
              <span className="text-red-500 font-bold">Pending Amount</span>
              <span className="text-red-500 font-bold">
                {formatCurrency(billingTotals.pending_amount)}
              </span>
            </div>

            {/* FIX #3: Show correct Refund Amount */}
            {billingTotals.total_refunded_amount > 0 && (
              <div className="flex justify-between mt-2">
                <span className="font-bold text-green-600">Refund Amount</span>
                <span className="font-bold text-green-600">
                  {formatCurrency(billingTotals.total_refunded_amount)}
                </span>
              </div>
            )}

            <div className="mt-4 space-y-2">
              <button
                onClick={handleProceedPayment}
                disabled={isProcessing || loading || isCheckingSignature}
                className={`bg-black text-white px-4 py-3 rounded w-full font-semibold text-lg ${
                  isProcessing || loading || isCheckingSignature
                    ? 'opacity-50 cursor-not-allowed'
                    : ''
                }`}
              >
                {isCheckingSignature
                  ? 'Checking...'
                  : isProcessing || loading
                    ? 'Processing...'
                    : buttonText}
              </button>
            </div>
          </div>
        </div>

        {/* Signature Prompt Modal */}
        {showSignaturePrompt && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <SignaturePrompt
              onSign={() => {
                setShowSignaturePrompt(false);
                setShowSignatureModal(true);
              }}
              onSkip={handleSkipSignature}
              onClose={() => {
                setShowSignaturePrompt(false);
                toast.info('Signature process cancelled', {
                  position: 'top-center',
                  autoClose: 3000,
                });
              }}
              showSkip={pendingAction !== 'billToCompany'}
              guestName={guestDetails.guestName}
              amount={formatCurrency(
                billingTotals.pending_amount || billingTotals.total_final_amount
              )}
            />
          </div>
        )}

        {/* Signature Modal */}
        <SignatureModal
          isOpen={showSignatureModal}
          onClose={() => {
            setShowSignatureModal(false);
            setShowSignaturePrompt(true);
          }}
          onSave={handleSignatureConfirm}
          guestName={guestDetails.guestName}
          amount={formatCurrency(billingTotals.pending_amount || billingTotals.total_final_amount)}
        />

        {/* Payment Options Modal */}
        {showPaymentOptions && !showErrorPopup && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg p-6 w-full max-w-md">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-bold">Select Payment Option</h2>
                <button
                  onClick={() => setShowPaymentOptions(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>

              <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                <div className="flex justify-between mb-2">
                  <span className="font-medium">Pending Amount:</span>
                  <span className="font-bold text-red-600">
                    {formatCurrency(billingTotals.pending_amount)}
                  </span>
                </div>
                <div className="text-sm text-gray-600">
                  {guestDetails.guestName} • Room {bookingDetails.roomNumber}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 mb-6">
                <button
                  onClick={() => handlePaymentOptionSelect('payNow')}
                  className={`p-4 rounded-lg border-2 flex flex-col items-center justify-center transition-all ${
                    selectedPaymentOption === 'payNow'
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-blue-300'
                  }`}
                >
                  <div
                    className={`p-3 rounded-full mb-2 ${selectedPaymentOption === 'payNow' ? 'bg-blue-100' : 'bg-gray-100'}`}
                  >
                    <svg
                      className="w-6 h-6 text-blue-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
                      />
                    </svg>
                  </div>
                  <span className="font-semibold">Pay Now</span>
                  <span className="text-xs text-gray-600 mt-1 text-center">
                    Secure online payment
                  </span>
                </button>

                <button
                  onClick={() =>
                    handlePaymentOptionSelect(isCorporateGuest ? 'billToCompany' : 'payAtFrontDesk')
                  }
                  className={`p-4 rounded-lg border-2 flex flex-col items-center justify-center transition-all ${
                    selectedPaymentOption ===
                    (isCorporateGuest ? 'billToCompany' : 'payAtFrontDesk')
                      ? 'border-purple-500 bg-purple-50'
                      : 'border-gray-200 hover:border-purple-300'
                  }`}
                >
                  <div
                    className={`p-3 rounded-full mb-2 ${selectedPaymentOption === (isCorporateGuest ? 'billToCompany' : 'payAtFrontDesk') ? 'bg-purple-100' : 'bg-gray-100'}`}
                  >
                    {isCorporateGuest ? (
                      <svg
                        className="w-6 h-6 text-purple-600"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                        />
                      </svg>
                    ) : (
                      <svg
                        className="w-6 h-6 text-purple-600"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M8 14v3m4-3v3m4-3v3M3 21h18M3 10h18M3 7l9-4 9 4M4 10h16v11H4V10z"
                        />
                      </svg>
                    )}
                  </div>
                  <span className="font-semibold">
                    {isCorporateGuest ? 'Bill to Company' : 'Pay at Front Desk'}
                  </span>
                  <span className="text-xs text-gray-600 mt-1 text-center">
                    {isCorporateGuest
                      ? `Invoice to ${guestDetails.companyName}`
                      : 'Pay during checkout'}
                  </span>
                </button>
              </div>

              <button
                onClick={() => {
                  if (selectedPaymentOption === 'payNow') {
                    handleRazorpayPayment();
                  } else if (selectedPaymentOption) {
                    handlePaymentOptionSelect(selectedPaymentOption);
                  } else {
                    toast.error('Please select a payment option');
                  }
                }}
                disabled={!selectedPaymentOption || isProcessing}
                className={`w-full py-3 rounded font-medium transition-colors ${
                  selectedPaymentOption && !isProcessing
                    ? 'bg-black text-white hover:bg-gray-800'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
              >
                {isProcessing ? 'Processing...' : 'Confirm Payment'}
              </button>
            </div>
          </div>
        )}
        <ToastContainer
          position="top-center"
          autoClose={5000}
          toastClassName="professional-toast"
        />
      </div>
    </div>
  );
};

export default CheckOutPayment;

// import React, { useState, useEffect } from "react";
// import { toast, ToastContainer } from "react-toastify";
// import "react-toastify/dist/ReactToastify.css";
// import { useNavigate } from "react-router-dom";
// import Cookies from "js-cookie";
// import SignatureModal from "../CHECK-IN/Reservation/Booking-Options/SignatureModal";
// import SignaturePrompt from "../CHECK-IN/Reservation/Booking-Options/SignaturePrompt";

// const BQ_BASE_URL = process.env.REACT_APP_BQ_BASE_URL;

// const REACT_APP_RAZORPAY_KEY = process.env.REACT_APP_RAZORPAY_KEY;

// const CheckOutPayment = () => {
//   const [billingSummary, setBillingSummary] = useState([]);
//   const [checkoutAmount, setCheckoutAmount] = useState(0);
//   const [isProcessing, setIsProcessing] = useState(false);
//   const [razorpayLoaded, setRazorpayLoaded] = useState(false);
//   const [bookingDetails, setBookingDetails] = useState({
//     checkInDate: "",
//     checkOutDate: "",
//     roomType: "",
//     roomNumber: "",
//   });
//   const [guestDetails, setGuestDetails] = useState({
//     guestName: "",
//     guestPhone: "",
//     guestEmail: "",
//     guestClientType: "",
//     companyName: "",
//     companyId: "",
//   });
//   const [hotelDetails, setHotelDetails] = useState({
//     hotelName: "",
//     address: "",
//     phone: "",
//     email: "",
//   });
//   const [billingTotals, setBillingTotals] = useState({
//     total_final_amount: 0,
//     total_base_amount: 0,
//     total_tax_amount: 0,
//     total_paid_amount: 0,
//     pending_amount: 0,
//     total_discount: 0,
//     gross_billed: 0,
//     total_credits: 0,
//     net_billed: 0,
//     total_refunded_amount: 0,
//   });
//   const [loading, setLoading] = useState(false);
//   const [error, setError] = useState(null);
//   const [expandedRows, setExpandedRows] = useState({});
//   const [serviceDetails, setServiceDetails] = useState({
//     amenities: [],
//     room_services: [],
//     food_orders: [],
//   });
//   const [showPaymentOptions, setShowPaymentOptions] = useState(false);
//   const [selectedPaymentOption, setSelectedPaymentOption] = useState(null);
//   const [isCorporateGuest, setIsCorporateGuest] = useState(false);
//   const [apiData, setApiData] = useState(null);
//   const [showErrorPopup, setShowErrorPopup] = useState(false);
//   const [errorMessage, setErrorMessage] = useState("");

//   // Signature states
//   const [showSignaturePrompt, setShowSignaturePrompt] = useState(false);
//   const [showSignatureModal, setShowSignatureModal] = useState(false);
//   const [signatureData, setSignatureData] = useState(null);
//   const [pendingAction, setPendingAction] = useState(null);
//   const [hasExistingSignature, setHasExistingSignature] = useState(false);
//   const [isCheckingSignature, setIsCheckingSignature] = useState(false);

//   const navigate = useNavigate();

//   const formatDate = (dateString) => {
//     if (!dateString) return "";
//     const date = new Date(dateString);
//     const day = String(date.getDate()).padStart(2, "0");
//     const month = String(date.getMonth() + 1).padStart(2, "0");
//     const year = date.getFullYear();
//     return `${day}-${month}-${year}`;
//   };

//   const getGuestIdFromToken = (token) => {
//     try {
//       const payload = JSON.parse(atob(token.split(".")[1]));
//       return payload.sub;
//     } catch (e) {
//       console.error("Error parsing token:", e);
//       return null;
//     }
//   };

//   const toggleRowExpand = (index) => {
//     setExpandedRows((prev) => ({
//       ...prev,
//       [index]: !prev[index],
//     }));
//   };

//   const formatCurrency = (amount) => {
//     return new Intl.NumberFormat("en-IN", {
//       style: "currency",
//       currency: "INR",
//       minimumFractionDigits: 2,
//       maximumFractionDigits: 2,
//     })
//       .format(amount)
//       .replace("₹", "₹");
//   };

//   // Check if signature already exists for this order
//   const checkExistingSignature = async (orderId) => {
//     if (!orderId) return false;

//     setIsCheckingSignature(true);
//     try {
//       const accessToken = Cookies.get("access_token");
//       const response = await fetch(
//         `${BQ_BASE_URL}/bq/api/get-signature/${orderId}?format=base64`,
//         {
//           method: "GET",
//           headers: {
//             Accept: "application/json",
//             Authorization: `Bearer ${accessToken}`,
//           },
//         }
//       );

//       if (response.status === 200) {
//         const data = await response.json();
//         if (data.success && data.signature) {
//           setHasExistingSignature(true);
//           setSignatureData(data.signature);
//           toast.info("Signature already exists for this booking", {
//             position: "top-center",
//             autoClose: 3000,
//           });
//           return true;
//         }
//       } else if (response.status === 404) {
//         // No signature found - this is expected
//         setHasExistingSignature(false);
//         return false;
//       }
//     } catch (error) {
//       console.error("Error checking signature:", error);
//       // Don't block the flow if check fails
//     } finally {
//       setIsCheckingSignature(false);
//     }
//     return false;
//   };

//   // Save signature to backend
//   const saveSignatureToBackend = async (signatureDataURL, orderId, guestName) => {
//     try {
//       // Convert data URL to blob
//       const response = await fetch(signatureDataURL);
//       const blob = await response.blob();

//       const formData = new FormData();
//       formData.append('order_id', orderId);
//       formData.append('guest_name', guestName);
//       formData.append('signature', blob, `signature_${Date.now()}.png`);
//       formData.append('storage_type', 'cloud');

//       const accessToken = Cookies.get("access_token");
//       const saveResponse = await fetch(
//         `${BQ_BASE_URL}/bq/api/save-signature`,
//         {
//           method: "POST",
//           headers: {
//             Authorization: `Bearer ${accessToken}`,
//           },
//           body: formData,
//         }
//       );

//       if (saveResponse.ok) {
//         const result = await saveResponse.json();
//         console.log("Signature saved successfully:", result);
//         toast.success("Signature saved successfully!", {
//           position: "top-center",
//           autoClose: 3000,
//         });
//         return true;
//       } else {
//         const error = await saveResponse.json();
//         console.error("Failed to save signature:", error);
//         toast.error("Failed to save signature. Please try again.", {
//           position: "top-center",
//           autoClose: 3000,
//         });
//         return false;
//       }
//     } catch (error) {
//       console.error("Error saving signature:", error);
//       toast.error("Failed to save signature. Please try again.", {
//         position: "top-center",
//         autoClose: 3000,
//       });
//       return false;
//     }
//   };

//   const extractServiceName = (description) => {
//     return description
//       .replace(
//         /^(Room Service|Amenity Item|Amenity Services|Food Order)\s*-\s*/i,
//         "",
//       )
//       .split(" x ")[0];
//   };

//   const extractQuantity = (description) => {
//     const match = description.match(/x\s*(\d+)/i);
//     return match ? parseInt(match[1], 10) : 1;
//   };

//   const transformBillingData = (data) => {
//     const billingItems = [];
//     const processedItems = new Set();

//     const roomServicesFromOrders = data.order_details?.["Room Services"] || [];

//     if (roomServicesFromOrders.length > 0) {
//       const itemsData = roomServicesFromOrders.map((order) => ({
//         ...order,
//         base_amount: order.amount,
//         tax_amount: order.tax,
//         discount: order.discount || 0,
//         total_amount: order.total_amount,
//         billing_type: order.order_type,
//         is_paid: order.payment_status === "Paid",
//         payment_status: order.payment_status,
//         name: extractServiceName(order.description),
//         quantity: extractQuantity(order.description),
//         created_at: order.created_at,
//         description: order.description,
//       }));

//       const totalBase = itemsData.reduce(
//         (sum, item) => sum + item.base_amount,
//         0,
//       );
//       const totalTax = itemsData.reduce(
//         (sum, item) => sum + item.tax_amount,
//         0,
//       );
//       const totalDiscount = itemsData.reduce(
//         (sum, item) => sum + (item.discount || 0),
//         0,
//       );
//       const totalAmount = itemsData.reduce(
//         (sum, item) => sum + item.total_amount,
//         0,
//       );
//       const isPaid = itemsData.every((item) => item.is_paid);

//       billingItems.push({
//         billing_id: "room-services-group",
//         billing_type: "Room Services",
//         base_amount: totalBase,
//         tax_amount: totalTax,
//         discount_amount: totalDiscount,
//         total_amount: totalAmount,
//         is_paid: isPaid,
//         payment_status: isPaid ? "Paid" : "Pending",
//         isGroup: true,
//         groupItems: itemsData,
//         sortOrder: 2,
//       });
//     }

//     const amenityServicesFromOrders =
//       data.order_details?.["Amenity Services"] || [];

//     if (amenityServicesFromOrders.length > 0) {
//       const itemsData = amenityServicesFromOrders.map((order) => ({
//         ...order,
//         base_amount: order.amount,
//         tax_amount: order.tax,
//         discount: order.discount || 0,
//         total_amount: order.total_amount,
//         billing_type: order.order_type,
//         is_paid: order.payment_status === "Paid",
//         payment_status: order.payment_status,
//         name: extractServiceName(order.description),
//         quantity: extractQuantity(order.description),
//         created_at: order.created_at,
//         description: order.description,
//       }));

//       const totalBase = itemsData.reduce(
//         (sum, item) => sum + item.base_amount,
//         0,
//       );
//       const totalTax = itemsData.reduce(
//         (sum, item) => sum + item.tax_amount,
//         0,
//       );
//       const totalDiscount = itemsData.reduce(
//         (sum, item) => sum + (item.discount || 0),
//         0,
//       );
//       const totalAmount = itemsData.reduce(
//         (sum, item) => sum + item.total_amount,
//         0,
//       );
//       const isPaid = itemsData.every((item) => item.is_paid);

//       billingItems.push({
//         billing_id: "amenity-services-group",
//         billing_type: "Amenity Service",
//         base_amount: totalBase,
//         tax_amount: totalTax,
//         discount_amount: totalDiscount,
//         total_amount: totalAmount,
//         is_paid: isPaid,
//         payment_status: isPaid ? "Paid" : "Pending",
//         isGroup: true,
//         groupItems: itemsData,
//         sortOrder: 3,
//       });
//     }

//     const foodOrdersFromOrders = data.order_details?.["Food"] || [];

//     if (foodOrdersFromOrders.length > 0) {
//       const itemsData = foodOrdersFromOrders.map((order) => ({
//         ...order,
//         base_amount: order.amount,
//         tax_amount: order.tax,
//         discount: order.discount || 0,
//         total_amount: order.total_amount,
//         billing_type: order.order_type,
//         is_paid: order.payment_status === "Paid",
//         payment_status: order.payment_status,
//         name: extractServiceName(order.description),
//         quantity: extractQuantity(order.description),
//         created_at: order.created_at,
//         description: order.description,
//       }));

//       const totalBase = itemsData.reduce(
//         (sum, item) => sum + item.base_amount,
//         0,
//       );
//       const totalTax = itemsData.reduce(
//         (sum, item) => sum + item.tax_amount,
//         0,
//       );
//       const totalDiscount = itemsData.reduce(
//         (sum, item) => sum + (item.discount || 0),
//         0,
//       );
//       const totalAmount = itemsData.reduce(
//         (sum, item) => sum + item.total_amount,
//         0,
//       );
//       const isPaid = itemsData.every((item) => item.is_paid);

//       billingItems.push({
//         billing_id: "food-orders-group",
//         billing_type: "Food Orders",
//         base_amount: totalBase,
//         tax_amount: totalTax,
//         discount_amount: totalDiscount,
//         total_amount: totalAmount,
//         is_paid: isPaid,
//         payment_status: isPaid ? "Paid" : "Pending",
//         isGroup: true,
//         groupItems: itemsData,
//         sortOrder: 4,
//       });
//     }

//     const roomChargesFromOrderDetails =
//       data.order_details?.["Room Charges"] || [];

//     if (roomChargesFromOrderDetails.length > 0) {
//       const totalBase = roomChargesFromOrderDetails.reduce(
//         (sum, item) => sum + item.amount,
//         0,
//       );
//       const totalTax = roomChargesFromOrderDetails.reduce(
//         (sum, item) => sum + item.tax,
//         0,
//       );
//       const totalServiceFee = roomChargesFromOrderDetails.reduce(
//         (sum, item) => sum + (item.service_fee || 0),
//         0,
//       );
//       const totalDiscount = roomChargesFromOrderDetails.reduce(
//         (sum, item) => sum + (item.discount || 0),
//         0,
//       );
//       const totalAmount = roomChargesFromOrderDetails.reduce(
//         (sum, item) => sum + item.total_amount,
//         0,
//       );
//       const isPaid = roomChargesFromOrderDetails.every(
//         (item) => item.payment_status === "Paid",
//       );

//       const itemsData = roomChargesFromOrderDetails.map((order) => {
//         const cleanedDescription = order.description
//           ? order.description
//               .replace(/\s*\(after\s*recalculation\)/gi, "")
//               .trim()
//           : order.order_type || "Room Rent";

//         return {
//           ...order,
//           base_amount: order.amount,
//           tax_amount: order.tax,
//           service_fee: order.service_fee || 0,
//           discount: order.discount || 0,
//           total_amount: order.total_amount,
//           billing_type: order.order_type,
//           is_paid: order.payment_status === "Paid",
//           payment_status: order.payment_status,
//           name: cleanedDescription,
//           quantity: 1,
//           created_at: order.created_at,
//           description: cleanedDescription,
//         };
//       });

//       billingItems.push({
//         billing_id: "room-charges-group-from-order-details",
//         billing_type: "Room Charges",
//         base_amount: totalBase,
//         tax_amount: totalTax,
//         service_fee: totalServiceFee,
//         discount_amount: totalDiscount,
//         total_amount: totalAmount,
//         is_paid: isPaid,
//         payment_status: isPaid ? "Paid" : "Pending",
//         isGroup: true,
//         groupItems: itemsData,
//         sortOrder: 0,
//       });
//     }

//     const securityDepositFromOrders =
//       data.order_details?.["Security Deposit"] || [];

//     if (securityDepositFromOrders.length > 0) {
//       const itemsData = securityDepositFromOrders.map((order) => ({
//         ...order,
//         base_amount: order.amount,
//         tax_amount: order.tax,
//         discount: order.discount || 0,
//         total_amount: order.total_amount,
//         billing_type: order.order_type,
//         is_paid: order.payment_status === "Paid",
//         payment_status: order.payment_status,
//         name: "Security Deposit",
//         quantity: 1,
//         created_at: order.created_at,
//       }));

//       const totalBase = itemsData.reduce(
//         (sum, item) => sum + item.base_amount,
//         0,
//       );
//       const totalTax = itemsData.reduce(
//         (sum, item) => sum + item.tax_amount,
//         0,
//       );
//       const totalDiscount = itemsData.reduce(
//         (sum, item) => sum + (item.discount || 0),
//         0,
//       );
//       const totalAmount = itemsData.reduce(
//         (sum, item) => sum + item.total_amount,
//         0,
//       );
//       const isPaid = itemsData.every((item) => item.is_paid);

//       billingItems.push({
//         billing_id: "security-deposit-group",
//         billing_type: "Security Deposit",
//         base_amount: totalBase,
//         tax_amount: totalTax,
//         discount_amount: totalDiscount,
//         total_amount: totalAmount,
//         is_paid: isPaid,
//         payment_status: isPaid ? "Paid" : "Pending",
//         isGroup: true,
//         groupItems: itemsData,
//         sortOrder: 1,
//       });
//     }

//     const otherCharges = data.billing_details.filter((item) =>
//       [
//         "Early Check-in",
//         "Late Check-out",
//         "Other",
//         "Enhance stay reservation",
//       ].includes(item.billing_type),
//     );

//     if (otherCharges.length > 0) {
//       const itemsData = otherCharges.map((item) => ({
//         ...item,
//         name: item.billing_type,
//         quantity: 1,
//         payment_status: item.is_paid ? "Paid" : "Pending",
//         created_at: item.payment_details[0]?.date || new Date().toISOString(),
//       }));

//       const totalBase = itemsData.reduce(
//         (sum, item) => sum + item.base_amount,
//         0,
//       );
//       const totalTax = itemsData.reduce(
//         (sum, item) => sum + item.tax_amount,
//         0,
//       );
//       const totalDiscount = itemsData.reduce(
//         (sum, item) => sum + (item.discount || 0),
//         0,
//       );
//       const totalAmount = itemsData.reduce(
//         (sum, item) => sum + item.total_amount,
//         0,
//       );
//       const isPaid = itemsData.every((item) => item.is_paid);

//       billingItems.push({
//         billing_id: "other-charges-group",
//         billing_type: "Other Charges",
//         base_amount: totalBase,
//         tax_amount: totalTax,
//         discount_amount: totalDiscount,
//         total_amount: totalAmount,
//         is_paid: isPaid,
//         payment_status: isPaid ? "Paid" : "Pending",
//         isGroup: true,
//         groupItems: itemsData,
//         sortOrder: 5,
//       });
//     }

//     return billingItems.sort((a, b) => {
//       if (a.sortOrder !== b.sortOrder) {
//         return a.sortOrder - b.sortOrder;
//       }
//       return a.billing_type.localeCompare(b.billing_type);
//     });
//   };

//   const getSortOrder = (billingType) => {
//     const sortOrderMap = {
//       "Room Charges": 0,
//       "Security Deposit": 1,
//       "Room Services": 2,
//       "Amenity Service": 3,
//       "Food Orders": 4,
//       "Other Charges": 5,
//       "Checkout Adjustments": 6,
//     };
//     return sortOrderMap[billingType] || 99;
//   };

//   const getItemName = (item, orderDetails) => {
//     for (const [category, items] of Object.entries(orderDetails)) {
//       const matchingOrder = items.find(
//         (order) =>
//           Math.abs(order.amount - item.base_amount) < 0.01 &&
//           Math.abs(order.tax - item.tax_amount) < 0.01,
//       );
//       if (matchingOrder) {
//         return extractServiceName(matchingOrder.description);
//       }
//     }
//     return item.billing_type;
//   };

//   const getItemQuantity = (item, orderDetails) => {
//     for (const [category, items] of Object.entries(orderDetails)) {
//       const matchingOrder = items.find(
//         (order) =>
//           Math.abs(order.amount - item.base_amount) < 0.01 &&
//           Math.abs(order.tax - item.tax_amount) < 0.01,
//       );
//       if (matchingOrder) {
//         return extractQuantity(matchingOrder.description);
//       }
//     }
//     return 1;
//   };

//   const sendInvoiceEmail = async () => {
//     try {
//       const orderDetails = JSON.parse(localStorage.getItem("orderDetails"));
//       const primaryGuest = JSON.parse(localStorage.getItem("primaryGuest"));

//       if (!orderDetails || !primaryGuest) {
//         console.warn(
//           "Order details or primary guest not found in localStorage",
//         );
//         return;
//       }

//       const orderId = orderDetails.orderid;
//       const recipientEmail = primaryGuest.emailid;

//       if (!orderId || !recipientEmail) {
//         console.warn("Order ID or recipient email not available");
//         return;
//       }

//       const accessToken = Cookies.get("access_token");
//       const response = await fetch(
//         `${BQ_BASE_URL}/bq/api/invoice/email/${orderId}?recipient_email=${encodeURIComponent(
//           recipientEmail,
//         )}`,
//         {
//           method: "POST",
//           headers: {
//             Accept: "application/json",
//             Authorization: `Bearer ${accessToken}`,
//           },
//         },
//       );

//       if (!response.ok) {
//         throw new Error("Failed to send invoice email");
//       }

//       const result = await response.json();
//       console.log("Invoice email sent successfully:", result);
//       return true;
//     } catch (error) {
//       console.error("Error sending invoice email:", error);
//       return false;
//     }
//   };

//   const fetchInvoiceData = async () => {
//     try {
//       const orderId = localStorage.getItem("orderId");
//       const accessToken = Cookies.get("access_token");

//       if (!orderId) {
//         throw new Error("Order ID not found in localStorage");
//       }

//       const response = await fetch(
//         `${BQ_BASE_URL}/bq/api/guest/billing-summary/?order_id=${orderId}`,
//         {
//           method: "GET",
//           headers: {
//             Accept: "application/json",
//             Authorization: `Bearer ${accessToken}`,
//           },
//         },
//       );

//       if (!response.ok) {
//         throw new Error(`Failed to fetch invoice data: ${response.statusText}`);
//       }

//       const data = await response.json();
//       setApiData(data);

//       const hotelCache = localStorage.getItem("hotelPropertyCache");
//       if (hotelCache) {
//         const hotelData = JSON.parse(hotelCache).data;
//         setHotelDetails({
//           hotelName: hotelData.propertyname,
//           address: hotelData.address,
//           phone: hotelData.phone,
//           email: hotelData.email,
//         });
//       }

//       if (data.guest_details) {
//         const guestData = data.guest_details;
//         setGuestDetails({
//           guestName: guestData.first_name + " " + guestData.last_name,
//           guestPhone: guestData.phone || "",
//           guestEmail: guestData.email || "",
//           guestClientType: guestData.client_type || "Leisure",
//           companyName: guestData.company_name || "",
//           companyId: guestData.company_id || "",
//         });

//         setIsCorporateGuest(guestData.client_type === "Corporate");
//       } else if (data.booking) {
//         setGuestDetails({
//           guestName: data.booking.guest_name || "",
//           guestPhone: "",
//           guestEmail: "",
//           guestClientType: "Leisure",
//           companyName: "",
//           companyId: "",
//         });
//         setIsCorporateGuest(false);
//       }

//       if (data.booking) {
//         setBookingDetails({
//           checkInDate: formatDate(data.booking.checkin_date),
//           checkOutDate: formatDate(data.booking.checkout_date),
//           roomType: data.booking.room_type || "",
//           roomNumber: data.booking.room_assigned?.room_number || "N/A",
//         });
//       }

//       const transformedBilling = transformBillingData(data);
//       setBillingSummary(transformedBilling);

//       let totalDiscount = 0;
//       if (data.billing_details && Array.isArray(data.billing_details)) {
//         totalDiscount = data.billing_details.reduce((sum, item) => {
//           return sum + (item.discount || 0);
//         }, 0);
//       }

//       if (data.payment_summary) {
//         const netBilled =
//           data.payment_summary.net_billed || data.payment_summary.total_amount;
//         const grossBilled =
//           data.payment_summary.gross_billed ||
//           data.payment_summary.total_amount;
//         const totalCredits = data.payment_summary.total_credits || 0;

//         setBillingTotals({
//           total_final_amount: netBilled,
//           total_base_amount: netBilled,
//           total_tax_amount: data.payment_summary.total_tax,
//           total_paid_amount: data.payment_summary.total_paid,
//           pending_amount: data.payment_summary.total_due,
//           total_discount: totalDiscount,
//           gross_billed: grossBilled,
//           total_credits: totalCredits,
//           net_billed: netBilled,
//           total_refunded_amount: data.payment_summary.total_refunded || 0,
//         });
//       } else if (data.total_amounts) {
//         const netBilled = data.total_amounts.net || data.total_amounts.final;
//         const grossBilled =
//           data.total_amounts.gross || data.total_amounts.final;
//         const totalCredits = data.total_amounts.credits || 0;

//         setBillingTotals({
//           total_final_amount: netBilled,
//           total_base_amount: data.total_amounts.base,
//           total_tax_amount: data.total_amounts.tax,
//           total_paid_amount: data.total_amounts.paid,
//           pending_amount: data.total_amounts.pending,
//           total_discount: totalDiscount,
//           gross_billed: grossBilled,
//           total_credits: totalCredits,
//           net_billed: netBilled,
//           total_refunded_amount: data.total_amounts.refunded || 0,
//         });
//       }

//       setServiceDetails({
//         amenities: data.order_details?.["Amenity Services"] || [],
//         room_services: data.order_details?.["Room Services"] || [],
//         food_orders: data.order_details?.["Food"] || [],
//       });

//       if (data.payment_summary?.total_due) {
//         setCheckoutAmount(data.payment_summary.total_due);
//       } else if (data.total_amounts?.pending) {
//         setCheckoutAmount(data.total_amounts.pending);
//       }

//       // After loading invoice data, check if signature exists
//       await checkExistingSignature(orderId);

//     } catch (error) {
//       console.error("Error fetching invoice data:", error);
//       toast.error("Failed to load invoice data. Please try again.");
//     }
//   };

//   // MODIFIED: Add signature prompt for zero amount
//   const handleProceedPayment = async () => {
//     // If signature already exists, skip the prompt
//     if (hasExistingSignature) {
//       if (billingTotals.pending_amount === 0) {
//         setPendingAction("checkout");
//         await handleZeroAmountCheckout();
//       } else {
//         setShowPaymentOptions(true);
//       }
//       return;
//     }

//     if (billingTotals.pending_amount === 0) {
//       // Fully paid - ask for signature before checkout
//       setPendingAction("checkout");
//       setShowSignaturePrompt(true);
//     } else {
//       // Not fully paid - show payment options
//       setShowPaymentOptions(true);
//     }
//   };

//   // ORIGINAL handleZeroAmountCheckout - KEPT EXACTLY THE SAME
//   const handleZeroAmountCheckout = async () => {
//     const orderId = localStorage.getItem("orderId");
//     const accessToken = Cookies.get("access_token");

//     if (!orderId) {
//       toast.error("Order ID not found. Please try again.");
//       return;
//     }

//     setLoading(true);

//     try {
//       const checkoutResponse = await fetch(
//         `${BQ_BASE_URL}/bq/api/check-out?orderid=${orderId}`,
//         {
//           method: "POST",
//           headers: {
//             Accept: "application/json",
//             "Content-Type": "application/json",
//             Authorization: `Bearer ${accessToken}`,
//           },
//           credentials: "include",
//         },
//       );

//       if (!checkoutResponse.ok) {
//         toast.error("Checkout failed. Please try again.");
//         setLoading(false);
//         return;
//       }

//       const checkoutData = await checkoutResponse.json();

//       localStorage.setItem("baseAmount", checkoutData.baseamount);
//       localStorage.setItem("finalAmount", checkoutData.finalamount);
//       localStorage.setItem("taxAmount", checkoutData.taxamount);

//       const orderDetails = {
//         orderid: orderId,
//         guest: {
//           guestid: getGuestIdFromToken(accessToken),
//           firstname: guestDetails.guestName.split(" ")[0],
//           lastname: guestDetails.guestName.split(" ")[1] || "",
//           emailid: guestDetails.guestEmail,
//           phonenumber: guestDetails.guestPhone,
//         },
//       };

//       localStorage.setItem("orderDetails", JSON.stringify(orderDetails));

//       await sendInvoiceEmail();

//       toast.success(
//         <div>
//           <strong>Thank you for staying with us!</strong>
//           <br />
//           {signatureData ? "Your signed invoice has been recorded. " : ""}
//           Please visit the front desk to return your keys.
//           <br />
//           You will receive your invoice via email shortly.
//         </div>,
//         {
//           position: "top-center",
//           autoClose: 7000,
//         },
//       );

//       // Clear signature states
//       setSignatureData(null);
//       setShowSignaturePrompt(false);
//       setShowSignatureModal(false);

//       navigate("/checkout-payment-success");
//     } catch (error) {
//       console.error("Error:", error);
//       toast.error("Checkout failed. Please try again.");
//       setLoading(false);
//     }
//   };

//   // MODIFIED: Make all options show SignaturePrompt first
//   const handlePaymentOptionSelect = async (option) => {
//     setSelectedPaymentOption(option);

//     // If signature already exists, proceed directly
//     if (hasExistingSignature) {
//       if (option === "payNow") {
//         await handleRazorpayPayment();
//       } else if (option === "billToCompany") {
//         await handlePayLater("billToCompany");
//       } else if (option === "payAtFrontDesk") {
//         await handlePayLater("payAtFrontDesk");
//       }
//       setShowPaymentOptions(false);
//       return;
//     }

//     if (option === "payNow") {
//       setPendingAction("payNow");
//       setShowSignaturePrompt(true);
//       setShowPaymentOptions(false);
//     } else if (option === "billToCompany") {
//       setPendingAction("billToCompany");
//       setShowSignaturePrompt(true);
//       setShowPaymentOptions(false);
//     } else if (option === "payAtFrontDesk") {
//       setPendingAction("payAtFrontDesk");
//       setShowSignaturePrompt(true);
//       setShowPaymentOptions(false);
//     }
//   };

//   // ORIGINAL handlePayLater - KEPT EXACTLY THE SAME
//   const handlePayLater = async (option) => {
//     if (isProcessing) return;

//     setIsProcessing(true);
//     setLoading(true);

//     try {
//       const orderId = localStorage.getItem("orderId");
//       const accessToken = Cookies.get("access_token");

//       if (!orderId) {
//         throw new Error("Order ID not found");
//       }

//       const checkoutResponse = await fetch(
//         `${BQ_BASE_URL}/bq/api/check-out?orderid=${orderId}`,
//         {
//           method: "POST",
//           headers: {
//             Accept: "application/json",
//             "Content-Type": "application/json",
//             Authorization: `Bearer ${accessToken}`,
//           },
//           credentials: "include",
//         },
//       );

//       if (!checkoutResponse.ok) {
//         toast.error("Checkout failed. Please try again.");
//         setIsProcessing(false);
//         setLoading(false);
//         return;
//       }

//       const checkoutData = await checkoutResponse.json();

//       localStorage.setItem("baseAmount", checkoutData.baseamount);
//       localStorage.setItem("finalAmount", checkoutData.finalamount);
//       localStorage.setItem("taxAmount", checkoutData.taxamount);

//       const orderDetails = {
//         orderid: orderId,
//         guest: {
//           guestid: getGuestIdFromToken(accessToken),
//           firstname: guestDetails.guestName.split(" ")[0],
//           lastname: guestDetails.guestName.split(" ")[1] || "",
//           emailid: guestDetails.guestEmail,
//           phonenumber: guestDetails.guestPhone,
//         },
//       };

//       localStorage.setItem("orderDetails", JSON.stringify(orderDetails));

//       setShowPaymentOptions(false);

//       // Clear signature states
//       setSignatureData(null);
//       setShowSignaturePrompt(false);
//       setShowSignatureModal(false);

//       navigate("/checkout-payment-success");
//     } catch (error) {
//       console.error("Payment error:", error);
//       toast.error("Checkout failed. Please try again.");
//       setIsProcessing(false);
//       setLoading(false);
//     }
//   };

//   // ORIGINAL handleRazorpayPayment - KEPT EXACTLY THE SAME
//   const handleRazorpayPayment = async () => {
//     if (isProcessing) return;

//     setIsProcessing(true);
//     setLoading(true);

//     try {
//       const orderId = localStorage.getItem("orderId");
//       const bookingId =
//         localStorage.getItem("bookingId") || apiData?.booking?.booking_id;
//       const accessToken = Cookies.get("access_token");

//       if (!orderId || !bookingId) {
//         throw new Error("Order or Booking ID not found");
//       }

//       const unpaidBillingIds =
//         apiData?.payment_summary?.unpaid_billing_ids || [];

//       if (unpaidBillingIds.length === 0) {
//         toast.error(
//           "No unpaid billing information available. Please try again.",
//         );
//         setIsProcessing(false);
//         setLoading(false);
//         return;
//       }

//       localStorage.setItem(
//         "unpaidBillingIds",
//         JSON.stringify(unpaidBillingIds),
//       );

//       if (!window.Razorpay) {
//         throw new Error("Payment gateway not loaded");
//       }

//       const orderResponse = await fetch(
//         `${BQ_BASE_URL}/bq/api/razorpay/create_payment_order_multiple?bookingid=${bookingId}`,
//         {
//           method: "POST",
//           headers: {
//             "Content-Type": "application/json",
//             Accept: "application/json",
//             Authorization: `Bearer ${accessToken}`,
//           },
//           body: JSON.stringify(unpaidBillingIds),
//         },
//       );

//       const orderData = await orderResponse.json();

//       if (!orderResponse.ok || !orderData?.razorpay_order_id) {
//         throw new Error("Failed to create payment order");
//       }

//       const razorpayAmount =
//         orderData.amount || billingTotals.pending_amount * 100;

//       const options = {
//         key: REACT_APP_RAZORPAY_KEY,
//         amount: razorpayAmount,
//         currency: "INR",
//         name: hotelDetails.hotelName || "Hotel Booking",
//         description: "Room Checkout Payment",
//         order_id: orderData.razorpay_order_id,
//         handler: async function (response) {
//           const paymentDetails = {
//             razorpay_order_id: response.razorpay_order_id,
//             razorpay_payment_id: response.razorpay_payment_id,
//             razorpay_signature: response.razorpay_signature,
//             billing_ids: unpaidBillingIds,
//             booking_id: bookingId,
//           };

//           try {
//             const verificationResponse = await fetch(
//               `${BQ_BASE_URL}/bq/api/razorpay/verify_payment_multiple`,
//               {
//                 method: "POST",
//                 headers: {
//                   "Content-Type": "application/json",
//                   Accept: "application/json",
//                   Authorization: `Bearer ${accessToken}`,
//                 },
//                 body: JSON.stringify(paymentDetails),
//               },
//             );

//             if (verificationResponse.ok) {
//               const verificationData = await verificationResponse.json();

//               if (verificationData.status === "success") {
//                 const checkoutResponse = await fetch(
//                   `${BQ_BASE_URL}/bq/api/check-out?orderid=${orderId}`,
//                   {
//                     method: "POST",
//                     headers: {
//                       Accept: "application/json",
//                       "Content-Type": "application/json",
//                       Authorization: `Bearer ${accessToken}`,
//                     },
//                     credentials: "include",
//                   },
//                 );

//                 if (!checkoutResponse.ok) {
//                   console.error("Checkout failed after successful payment");
//                   toast.error(
//                     "Payment succeeded but checkout failed. Please contact support.",
//                   );
//                   setShowPaymentOptions(false);

//                   // Clear signature states
//                   setSignatureData(null);
//                   setShowSignaturePrompt(false);
//                   setShowSignatureModal(false);

//                   navigate("/checkout-payment-success");
//                   return;
//                 }

//                 const checkoutData = await checkoutResponse.json();

//                 localStorage.setItem("baseAmount", checkoutData.baseamount);
//                 localStorage.setItem("finalAmount", checkoutData.finalamount);
//                 localStorage.setItem("taxAmount", checkoutData.taxamount);
//                 localStorage.setItem("bookingId", checkoutData.bookingid);
//                 localStorage.setItem(
//                   "finalPaymentStatus",
//                   checkoutData.finalpaymentstatus,
//                 );

//                 const orderDetails = {
//                   orderid: orderId,
//                   guest: {
//                     guestid: getGuestIdFromToken(accessToken),
//                     firstname: guestDetails.guestName.split(" ")[0],
//                     lastname: guestDetails.guestName.split(" ")[1] || "",
//                     emailid: guestDetails.guestEmail,
//                     phonenumber: guestDetails.guestPhone,
//                   },
//                 };

//                 localStorage.setItem(
//                   "orderDetails",
//                   JSON.stringify(orderDetails),
//                 );

//                 await sendInvoiceEmail();

//                 toast.success(
//                   <div>
//                     <strong>Thank you for staying with us!</strong>
//                     <br />
//                     {signatureData
//                       ? "Your signed invoice has been recorded. "
//                       : ""}
//                     Please visit the front desk to return your keys.
//                     <br />
//                     You will receive your invoice via email shortly.
//                   </div>,
//                   {
//                     position: "top-center",
//                     autoClose: 7000,
//                   },
//                 );

//                 setShowPaymentOptions(false);

//                 // Clear signature states
//                 setSignatureData(null);
//                 setShowSignaturePrompt(false);
//                 setShowSignatureModal(false);

//                 navigate("/checkout-payment-success");
//               } else {
//                 throw new Error("Payment verification failed");
//               }
//             } else {
//               throw new Error("Payment verification failed");
//             }
//           } catch (error) {
//             console.error("Verification error:", error);
//             toast.error("Payment verification failed. Please contact support.");
//             setIsProcessing(false);
//             setLoading(false);
//           }
//         },
//         modal: {
//           ondismiss: function () {
//             console.log("Payment modal closed by user");
//             setIsProcessing(false);
//             setLoading(false);
//             toast.info("Payment cancelled");
//           },
//         },
//         prefill: {
//           name: guestDetails.guestName,
//           email: guestDetails.guestEmail,
//           contact: guestDetails.guestPhone?.toString() || "",
//         },
//         theme: { color: "#3399cc" },
//         notes: {
//           bookingId: bookingId,
//           billingIds: unpaidBillingIds.join(","),
//           orderId: orderId,
//         },
//       };

//       const rzp = new window.Razorpay(options);
//       rzp.open();
//     } catch (error) {
//       console.error("Payment error:", error);
//       toast.error("Payment failed. Please try again.");
//       setIsProcessing(false);
//       setLoading(false);
//     }
//   };

//   // UPDATED: Handle signature confirmation with backend save
//   const handleSignatureConfirm = async (signatureDataURL) => {
//     setSignatureData(signatureDataURL);
//     setShowSignatureModal(false);
//     setShowSignaturePrompt(false);

//     // Save signature to backend
//     const orderId = localStorage.getItem("orderId");
//     if (orderId && signatureDataURL) {
//       const saved = await saveSignatureToBackend(signatureDataURL, orderId, guestDetails.guestName);
//       if (saved) {
//         setHasExistingSignature(true);
//       }
//     }

//     // Execute the pending action after signature is captured
//     if (pendingAction === "checkout") {
//       handleZeroAmountCheckout();
//     } else if (pendingAction === "payNow") {
//       handleRazorpayPayment();
//     } else if (pendingAction === "billToCompany") {
//       handlePayLater("billToCompany");
//     } else if (pendingAction === "payAtFrontDesk") {
//       handlePayLater("payAtFrontDesk");
//     }
//   };

//   // NEW: Handle skip signature
//   const handleSkipSignature = () => {
//     setShowSignaturePrompt(false);

//     // Execute the pending action without signature
//     if (pendingAction === "checkout") {
//       handleZeroAmountCheckout();
//     } else if (pendingAction === "payNow") {
//       handleRazorpayPayment();
//     } else if (pendingAction === "payAtFrontDesk") {
//       handlePayLater("payAtFrontDesk");
//     }
//     // For billToCompany, signature is required so we don't skip
//   };

//   const renderServiceDetails = (billingType, groupItems) => {
//     const getItemValues = (item) => {
//       return {
//         base: item.base_amount !== undefined ? item.base_amount : item.amount,
//         tax: item.tax_amount !== undefined ? item.tax_amount : item.tax,
//         service_fee: item.service_fee || 0,
//         discount: item.discount || 0,
//         total: item.total_amount,
//         date: item.created_at,
//       };
//     };

//     const renderTable = (items, columns, showQuantity = true) => {
//       return (
//         <div className="mt-2 ml-4 p-2 bg-gray-50 rounded">
//           <h4 className="font-semibold mb-2">{billingType} Details</h4>
//           <table className="w-full">
//             <thead>
//               <tr className="bg-gray-100">
//                 {columns.map((col, idx) => (
//                   <th key={idx} className="text-left p-2">
//                     {col}
//                   </th>
//                 ))}
//               </tr>
//             </thead>
//             <tbody>
//               {items.map((item, idx) => {
//                 const values = getItemValues(item);

//                 const displayName =
//                   billingType === "Room Charges"
//                     ? item.description || item.name || item.billing_type
//                     : item.name ||
//                       extractServiceName(item.description || item.billing_type);

//                 return (
//                   <tr key={idx} className="border-b">
//                     <td className="p-2">{displayName}</td>
//                     <td className="p-2">{formatDate(values.date)}</td>
//                     {showQuantity && (
//                       <td className="p-2">{item.quantity || 1}</td>
//                     )}
//                     <td className="p-2">{formatCurrency(values.base)}</td>
//                     <td className="p-2">{formatCurrency(values.tax)}</td>
//                     {columns.includes("Service Fee") && (
//                       <td className="p-2">
//                         {formatCurrency(values.service_fee)}
//                       </td>
//                     )}
//                     {columns.includes("Discount") && (
//                       <td className="p-2 text-green-600">
//                         {formatCurrency(values.discount)}
//                       </td>
//                     )}
//                     <td className="p-2">{formatCurrency(values.total)}</td>
//                   </tr>
//                 );
//               })}
//             </tbody>
//           </table>
//         </div>
//       );
//     };

//     if (billingType === "Room Charges") {
//       return renderTable(
//         groupItems,
//         [
//           "Description",
//           "Date",
//           "Quantity",
//           "Price",
//           "Tax",
//           "Service Fee",
//           "Total",
//         ],
//         true,
//       );
//     }

//     return renderTable(
//       groupItems,
//       ["Item", "Date", "Quantity", "Price", "Tax", "Total"],
//       true,
//     );
//   };

//   const buttonText =
//     billingTotals.pending_amount === 0
//       ? "Checkout"
//       : loading
//         ? "Processing..."
//         : "Proceed Payment";

//   useEffect(() => {
//     fetchInvoiceData();

//     const loadRazorpayScript = () => {
//       const script = document.createElement("script");
//       script.src = "https://checkout.razorpay.com/v1/checkout.js";
//       script.async = true;
//       script.onload = () => setRazorpayLoaded(true);
//       script.onerror = () => {
//         toast.error("Error loading Razorpay script. Please try again.");
//       };
//       document.body.appendChild(script);
//     };

//     if (!window.Razorpay) {
//       loadRazorpayScript();
//     } else {
//       setRazorpayLoaded(true);
//     }
//   }, []);

//   return (
//     <div className="flex w-full bg-gray-50 p-2 flex-col">
//       <div className="mb-6">
//         <h1 className="text-xl font-bold mb-1 text-gray-900">Payment</h1>
//         <p className="text-gray-600 text-sm mb- mt-">
//           Review and complete your payment
//         </p>
//       </div>

//       <div className="flex w-full">
//         <main className="flex-1 overflow-y-auto mr-4">
//           <div
//             className="bg-white p-6 rounded-lg shadow-md flex flex-col"
//             style={{ minHeight: "calc(100vh - 8rem)" }}
//           >
//             <div className="flex-grow">
//               <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10">
//                 <div className="flex flex-col gap-2">
//                   <p className="text-xl font-bold text-black">Guest Details</p>
//                   <div className="grid grid-cols-1 gap-y-2 text-gray-800">
//                     <div>
//                       <span className="font-semibold">Name:</span>{" "}
//                       {guestDetails.guestName}
//                     </div>
//                     {guestDetails.guestEmail && (
//                       <div>
//                         <span className="font-semibold">Email:</span>{" "}
//                         {guestDetails.guestEmail}
//                       </div>
//                     )}
//                     <div>
//                       <span className="font-semibold">Client Type:</span>{" "}
//                       {guestDetails.guestClientType}
//                     </div>
//                     {isCorporateGuest && guestDetails.companyName && (
//                       <div>
//                         <span className="font-semibold">Company:</span>{" "}
//                         {guestDetails.companyName}
//                       </div>
//                     )}
//                   </div>
//                 </div>

//                 <div className="flex flex-col gap-2">
//                   <p className="text-xl font-bold text-black">
//                     Reservation Details
//                   </p>
//                   <div className="grid grid-cols-2 gap-x-8 gap-y-2 text-gray-800">
//                     <div className="font-semibold">
//                       Room No: {bookingDetails.roomNumber}
//                     </div>
//                     <div className="font-semibold">
//                       Room Type: {bookingDetails.roomType}
//                     </div>
//                     <div className="font-semibold">
//                       Check-In: {bookingDetails.checkInDate}
//                     </div>
//                     <div className="font-semibold">
//                       Check-Out: {bookingDetails.checkOutDate}
//                     </div>
//                   </div>
//                 </div>
//               </div>

//               <div className="overflow-x-auto">
//                 <table className="w-full border-collapse">
//                   <thead>
//                     <tr className="bg-gray-100">
//                       <th className="py-3 px-4 text-left text-base font-medium text-gray-600">
//                         Billing Type
//                       </th>
//                       <th className="py-3 px-4 text-left text-base font-medium text-gray-600">
//                         Price
//                       </th>
//                       <th className="py-3 px-4 text-left text-base font-medium text-gray-600">
//                         Tax
//                       </th>
//                       <th className="py-3 px-4 text-left text-base font-medium text-gray-600">
//                         Discount
//                       </th>
//                       <th className="py-3 px-4 text-left text-base font-medium text-gray-600">
//                         Service Fee
//                       </th>
//                       <th className="py-3 px-4 text-left text-base font-medium text-gray-600">
//                         Final Price
//                       </th>
//                     </tr>
//                   </thead>
//                   <tbody>
//                     {billingSummary.map((item, index) => (
//                       <React.Fragment key={index}>
//                         <tr
//                           className="border-b hover:bg-gray-50 transition-colors cursor-pointer"
//                           onClick={() => toggleRowExpand(index)}
//                         >
//                           <td className="py-3 px-4 text-base text-gray-700">
//                             {item.billing_type}
//                             {item.isGroup && (
//                               <span className="ml-2 text-gray-500">
//                                 ({expandedRows[index] ? "Hide" : "Show"}{" "}
//                                 details)
//                               </span>
//                             )}
//                           </td>
//                           <td className="py-3 px-4 text-base text-gray-700">
//                             {formatCurrency(item.base_amount)}
//                           </td>
//                           <td className="py-3 px-4 text-base text-gray-700">
//                             {formatCurrency(item.tax_amount)}
//                           </td>
//                           <td className="py-3 px-4 text-base text-green-600">
//                             {formatCurrency(item.discount_amount || 0)}
//                           </td>
//                           <td className="py-3 px-4 text-base text-gray-700">
//                             {item.billing_type === "Room Charges"
//                               ? formatCurrency(item.service_fee || 0)
//                               : "-"}
//                           </td>
//                           <td className="py-3 px-4 text-base text-gray-700">
//                             {formatCurrency(item.total_amount)}
//                           </td>
//                         </tr>
//                         {expandedRows[index] && item.isGroup && (
//                           <tr>
//                             <td colSpan="6" className="p-0">
//                               {renderServiceDetails(
//                                 item.billing_type,
//                                 item.groupItems,
//                               )}
//                             </td>
//                           </tr>
//                         )}
//                       </React.Fragment>
//                     ))}
//                   </tbody>
//                 </table>
//               </div>
//             </div>

//             <div className="mt-auto pt-4 border-t border-gray-200">
//               <h3 className="text-base font-semibold text-gray-600">
//                 Terms & Conditions
//               </h3>
//               <p className="text-base text-gray-500">
//                 Payment is due within 7 days of checkout. Late payments may
//                 incur additional fees.
//               </p>
//             </div>
//           </div>
//         </main>

//         <div className="w-1/4">
//           <div className="bg-white p-6 rounded-lg shadow-md flex flex-col">
//             <h2 className="text-xl font-bold mb-4">Payment Summary</h2>
//             <div className="space-y-2 flex-grow">
//               <div className="flex justify-between">
//                 <span>Total Amount</span>
//                 <span>{formatCurrency(billingTotals.total_base_amount)}</span>
//               </div>

//               {billingTotals.total_discount > 0 && (
//                 <div className="flex justify-between text-green-600">
//                   <span>Total Discount</span>
//                   <span>{formatCurrency(billingTotals.total_discount)}</span>
//                 </div>
//               )}

//               <div className="flex justify-between">
//                 <span>Amount Paid</span>
//                 <span>{formatCurrency(billingTotals.total_paid_amount)}</span>
//               </div>

//               {billingTotals.total_credits > 0 && (
//                 <div className="flex justify-between">
//                   <span>Adjustment Amount</span>
//                   <span className="text-green-600">
//                     -{formatCurrency(Math.abs(billingTotals.total_credits))}
//                   </span>
//                 </div>
//               )}
//             </div>

//             <hr className="my-3" />

//             <div className="flex justify-between">
//               <span className="text-red-500 font-bold">Pending Amount</span>
//               <span className="text-red-500 font-bold">
//                 {formatCurrency(billingTotals.pending_amount)}
//               </span>
//             </div>

//             {billingTotals.total_refunded_amount > 0 && (
//               <div className="flex justify-between mt-2">
//                 <span className="font-bold text-green-600">Refund Amount</span>
//                 <span className="font-bold text-green-600">
//                   {formatCurrency(billingTotals.total_refunded_amount)}
//                 </span>
//               </div>
//             )}

//             <div className="mt-4 space-y-2">
//               <button
//                 onClick={handleProceedPayment}
//                 disabled={isProcessing || loading || isCheckingSignature}
//                 className={`bg-black text-white px-4 py-3 rounded w-full font-semibold text-lg ${
//                   isProcessing || loading || isCheckingSignature ? "opacity-50 cursor-not-allowed" : ""
//                 }`}
//               >
//                 {isCheckingSignature ? "Checking..." : isProcessing || loading ? "Processing..." : buttonText}
//               </button>
//             </div>
//           </div>
//         </div>

//         {/* Signature Prompt Modal */}
//         {showSignaturePrompt && (
//           <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
//             <SignaturePrompt
//               onSign={() => {
//                 setShowSignaturePrompt(false);
//                 setShowSignatureModal(true);
//               }}
//               onSkip={handleSkipSignature}
//               onClose={() => {
//                 setShowSignaturePrompt(false);
//                 toast.info("Signature process cancelled", {
//                   position: "top-center",
//                   autoClose: 3000,
//                 });
//               }}
//               showSkip={pendingAction !== "billToCompany"}
//               guestName={guestDetails.guestName}
//               amount={formatCurrency(
//                 billingTotals.pending_amount ||
//                   billingTotals.total_final_amount,
//               )}
//             />
//           </div>
//         )}

//         {/* Signature Modal */}
//         <SignatureModal
//           isOpen={showSignatureModal}
//           onClose={() => {
//             setShowSignatureModal(false);
//             setShowSignaturePrompt(true);
//           }}
//           onSave={handleSignatureConfirm}
//           guestName={guestDetails.guestName}
//           amount={formatCurrency(
//             billingTotals.pending_amount || billingTotals.total_final_amount,
//           )}
//         />

//         {/* Payment Options Modal */}
//         {showPaymentOptions && !showErrorPopup && (
//           <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
//             <div className="bg-white rounded-lg p-6 w-full max-w-md">
//               <div className="flex justify-between items-center mb-6">
//                 <h2 className="text-xl font-bold">Select Payment Option</h2>
//                 <button
//                   onClick={() => setShowPaymentOptions(false)}
//                   className="text-gray-500 hover:text-gray-700"
//                 >
//                   <svg
//                     className="w-6 h-6"
//                     fill="none"
//                     stroke="currentColor"
//                     viewBox="0 0 24 24"
//                   >
//                     <path
//                       strokeLinecap="round"
//                       strokeLinejoin="round"
//                       strokeWidth={2}
//                       d="M6 18L18 6M6 6l12 12"
//                     />
//                   </svg>
//                 </button>
//               </div>

//               <div className="mb-6 p-4 bg-gray-50 rounded-lg">
//                 <div className="flex justify-between mb-2">
//                   <span className="font-medium">Pending Amount:</span>
//                   <span className="font-bold text-red-600">
//                     {formatCurrency(billingTotals.pending_amount)}
//                   </span>
//                 </div>
//                 <div className="text-sm text-gray-600">
//                   {guestDetails.guestName} • Room {bookingDetails.roomNumber}
//                 </div>
//               </div>

//               <div className="grid grid-cols-2 gap-4 mb-6">
//                 <button
//                   onClick={() => handlePaymentOptionSelect("payNow")}
//                   className={`p-4 rounded-lg border-2 flex flex-col items-center justify-center transition-all ${
//                     selectedPaymentOption === "payNow"
//                       ? "border-blue-500 bg-blue-50"
//                       : "border-gray-200 hover:border-blue-300"
//                   }`}
//                 >
//                   <div
//                     className={`p-3 rounded-full mb-2 ${selectedPaymentOption === "payNow" ? "bg-blue-100" : "bg-gray-100"}`}
//                   >
//                     <svg
//                       className="w-6 h-6 text-blue-600"
//                       fill="none"
//                       stroke="currentColor"
//                       viewBox="0 0 24 24"
//                     >
//                       <path
//                         strokeLinecap="round"
//                         strokeLinejoin="round"
//                         strokeWidth={2}
//                         d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
//                       />
//                     </svg>
//                   </div>
//                   <span className="font-semibold">Pay Now</span>
//                   <span className="text-xs text-gray-600 mt-1 text-center">
//                     Secure online payment
//                   </span>
//                 </button>

//                 <button
//                   onClick={() =>
//                     handlePaymentOptionSelect(
//                       isCorporateGuest ? "billToCompany" : "payAtFrontDesk",
//                     )
//                   }
//                   className={`p-4 rounded-lg border-2 flex flex-col items-center justify-center transition-all ${
//                     selectedPaymentOption ===
//                     (isCorporateGuest ? "billToCompany" : "payAtFrontDesk")
//                       ? "border-purple-500 bg-purple-50"
//                       : "border-gray-200 hover:border-purple-300"
//                   }`}
//                 >
//                   <div
//                     className={`p-3 rounded-full mb-2 ${selectedPaymentOption === (isCorporateGuest ? "billToCompany" : "payAtFrontDesk") ? "bg-purple-100" : "bg-gray-100"}`}
//                   >
//                     {isCorporateGuest ? (
//                       <svg
//                         className="w-6 h-6 text-purple-600"
//                         fill="none"
//                         stroke="currentColor"
//                         viewBox="0 0 24 24"
//                       >
//                         <path
//                           strokeLinecap="round"
//                           strokeLinejoin="round"
//                           strokeWidth={2}
//                           d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
//                         />
//                       </svg>
//                     ) : (
//                       <svg
//                         className="w-6 h-6 text-purple-600"
//                         fill="none"
//                         stroke="currentColor"
//                         viewBox="0 0 24 24"
//                       >
//                         <path
//                           strokeLinecap="round"
//                           strokeLinejoin="round"
//                           strokeWidth={2}
//                           d="M8 14v3m4-3v3m4-3v3M3 21h18M3 10h18M3 7l9-4 9 4M4 10h16v11H4V10z"
//                         />
//                       </svg>
//                     )}
//                   </div>
//                   <span className="font-semibold">
//                     {isCorporateGuest ? "Bill to Company" : "Pay at Front Desk"}
//                   </span>
//                   <span className="text-xs text-gray-600 mt-1 text-center">
//                     {isCorporateGuest
//                       ? `Invoice to ${guestDetails.companyName}`
//                       : "Pay during checkout"}
//                   </span>
//                 </button>
//               </div>

//               <button
//                 onClick={() => {
//                   if (selectedPaymentOption === "payNow") {
//                     handleRazorpayPayment();
//                   } else if (selectedPaymentOption) {
//                     handlePaymentOptionSelect(selectedPaymentOption);
//                   } else {
//                     toast.error("Please select a payment option");
//                   }
//                 }}
//                 disabled={!selectedPaymentOption || isProcessing}
//                 className={`w-full py-3 rounded font-medium transition-colors ${
//                   selectedPaymentOption && !isProcessing
//                     ? "bg-black text-white hover:bg-gray-800"
//                     : "bg-gray-300 text-gray-500 cursor-not-allowed"
//                 }`}
//               >
//                 {isProcessing ? "Processing..." : "Confirm Payment"}
//               </button>
//             </div>
//           </div>
//         )}
//         <ToastContainer
//           position="top-center"
//           autoClose={5000}
//           toastClassName="professional-toast"
//         />
//       </div>
//     </div>
//   );

// };

// export default CheckOutPayment;
